import React, { useEffect, useState, useCallback, useMemo, useRef } from "react";
import { createPortal } from "react-dom";
import "./WeekPicker.css";
import {
  fetchProjects,
  fetchTimeEntries, createTimeEntry, updateTimeEntry, deleteTimeEntry,
  fetchRunningTimer, startTimer, stopTimer, toggleTimerProject, setEntryProjectTimes,
  fetchHolidays,
  fetchVacations, createVacation,
  fetchMe,
  getWsUrl, getWsToken,
} from "../api";
import { MONTHS_SHORT, MONTH_NAMES, WEEKDAYS } from "../constants";
import ConfirmDialog from "./ConfirmDialog";
import DatePicker from "./DatePicker";
import MiniCalendar from "./MiniCalendar";
import {
  pad2, getMonday, addDays, formatISODate, parseISODateLocal, fmtDateDE,
  combineDateTime, weekKeyOf, isoWeekday, getISOWeek as getWeekNumber, getISOWeekYear,
  getMondayOfWeek, lastWeekOfYear, monthOfWeek, useToday,
} from "../lib/dates";
import { fmtDuration } from "../lib/format";
import {
  entryDurationMs, holidayDateSet, countsAsTracked, dailyTargetMs,
  buildHolidayEntries, buildVacationEntries, weekTargetMs, accountBalanceMs,
  activePhaseOf, scopeUserToPhase, scopeEntriesToPhase, clipVacationsToPhase,
  vacationAccountForPhase, countedVacDays,
} from "../lib/timeMath";
import "./AppointmentForm.css";
import "./Time.css";

const WEEKDAYS_LONG = ["Montag", "Dienstag", "Mittwoch", "Donnerstag", "Freitag", "Samstag", "Sonntag"];

const TIME_DROPDOWN_OPTIONS = (() => {
  const opts = [];
  for (let h = 6; h <= 22; h++) {
    for (let m = 0; m < 60; m += 15) {
      opts.push(`${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}`);
    }
  }
  return opts;
})();

// time entry types (keys are the persisted values; labels stay German for the UI)
const KIND_ORDER = ["work", "vacation", "sick", "holiday"];
const KINDS = {
  work: { label: "Arbeit", color: "#4f8fd0" },
  vacation: { label: "Urlaub", color: "#8a6fd4" },
  sick: { label: "Krank", color: "#2bb3a3" },
  holiday: { label: "Feiertag", color: "#6c7fd2" },
};
function kindOf(e) { return KINDS[e.kind] ? e.kind : "work"; }

// Der auf Projekte gebuchte Anteil der Arbeitszeit — als eigenes Segment im
// Balken, damit man sieht, wie viel des Tages auf Projekte lief. Die Summe
// bleibt unverändert: Projekt ist ein AUSSCHNITT von "Arbeit", kein Zusatz.
const PROJECT_COLOR = "#5aa46a";

// Segmente EINES Tagesbalkens, von unten nach oben (der Balken ist
// column-reverse, das erste Element sitzt unten): erst der Projektanteil (grün),
// dann die restliche Arbeitszeit (blau), darüber die Gutschrift-Arten.
// Haupt- und Mini-Graph teilen sich diese Funktion — sonst driften sie auseinander.
function barSegments(byKind, projectMs, totalMs) {
  if (!totalMs) return [];
  const out = [];
  for (const k of KIND_ORDER) {
    const ms = byKind[k] || 0;
    if (ms <= 0) continue;
    if (k === "work") {
      const proj = Math.min(projectMs || 0, ms);
      if (proj > 0) out.push({ key: "project", pct: (proj / totalMs) * 100, color: PROJECT_COLOR });
      if (ms - proj > 0) out.push({ key: "work", pct: ((ms - proj) / totalMs) * 100, color: KINDS.work.color });
    } else {
      out.push({ key: k, pct: (ms / totalMs) * 100, color: KINDS[k].color });
    }
  }
  return out;
}

// ---- Project tag ordering (by colour) ---------------------------------------
// Hex → HSL. Returns null for anything that isn't a #rgb/#rrggbb colour.
function hexToHsl(hex) {
  const m = /^#?([0-9a-f]{3}|[0-9a-f]{6})$/i.exec(String(hex || "").trim());
  if (!m) return null;
  let v = m[1];
  if (v.length === 3) v = v[0] + v[0] + v[1] + v[1] + v[2] + v[2];
  const r = parseInt(v.slice(0, 2), 16) / 255;
  const g = parseInt(v.slice(2, 4), 16) / 255;
  const b = parseInt(v.slice(4, 6), 16) / 255;
  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  const l = (max + min) / 2;
  const d = max - min;
  if (d === 0) return { h: 0, s: 0, l };
  const s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
  let h;
  if (max === r) h = ((g - b) / d) % 6;
  else if (max === g) h = (b - r) / d + 2;
  else h = (r - g) / d + 4;
  return { h: (h * 60 + 360) % 360, s, l };
}

// Sort key for a project tag: the colour wheel rotated so yellow (~50°) is 0 —
// yellows first, then greens, teals, blues/violets; reds/oranges wrap to the
// end. Grey / colourless projects have no meaningful hue and go last.
const TAG_GREY_SATURATION = 0.12;
const TAG_RANK_LAST = 1e6; // finite, so comparing two grey tags can't produce NaN
function projectTagRank(p) {
  const hsl = hexToHsl(p.client_color || p.color);
  if (!hsl || hsl.s <= TAG_GREY_SATURATION) return TAG_RANK_LAST;
  return (hsl.h - 50 + 360) % 360;
}

// parse loosely typed time input ("0123" / "123" / "9:30" → "01:23" / "01:23" / "09:30")
function parseTimeDigits(str) {
  const digits = String(str).replace(/\D/g, "");
  if (digits.length === 0) return null;
  let h, m;
  if (digits.length <= 2) { h = parseInt(digits, 10); m = 0; }
  else {
    h = parseInt(digits.slice(0, -2), 10);
    m = parseInt(digits.slice(-2), 10);
  }
  if (Number.isNaN(h) || Number.isNaN(m) || h > 23 || m > 59) return null;
  return `${pad2(h)}:${pad2(m)}`;
}

/** Duration field for a project's share of an entry.
 *
 * Keeps what the user typed in local state while the field has focus. Formatting
 * the committed value on every keystroke (the previous behaviour) rewrote "3" to
 * "00:00" mid-typing, which made the field impossible to fill in. */
function PtTimeInput({ seconds, onChange }) {
  const [draft, setDraft] = useState(null);
  return (
    <input
      type="text"
      className="wf-input wf-form-pt-time"
      value={draft ?? fmtSecondsAsHHMM(seconds)}
      maxLength={5}
      inputMode="numeric"
      onFocus={(e) => { setDraft(fmtSecondsAsHHMM(seconds)); e.target.select(); }}
      onChange={(e) => {
        setDraft(e.target.value);
        const secs = parseDurationToSeconds(e.target.value);
        if (secs != null) onChange(secs);
      }}
      onBlur={() => {
        // Empty or unparseable input falls back to zero rather than silently
        // keeping the previous value the user just cleared.
        if (draft != null && parseDurationToSeconds(draft) == null) onChange(0);
        setDraft(null);
      }}
      onKeyDown={(e) => { if (e.key === "Enter") e.currentTarget.blur(); }}
    />
  );
}

// parse a duration like "2:30" or "0230" into total seconds
function parseDurationToSeconds(str) {
  const t = parseTimeDigits(str);
  if (!t) return null;
  const [h, m] = t.split(":").map(Number);
  return h * 3600 + m * 60;
}

function fmtSecondsAsHHMM(seconds) {
  const totalMin = Math.round((seconds || 0) / 60);
  return `${pad2(Math.floor(totalMin / 60))}:${pad2(totalMin % 60)}`;
}

function timeInputFromISO(iso) {
  const d = new Date(iso);
  return `${pad2(d.getHours())}:${pad2(d.getMinutes())}`;
}

function dateInputFromISO(iso) {
  return formatISODate(new Date(iso));
}

// Same output as fmtSecondsAsHHMM, ms input — one rounding implementation.
function fmtClock(ms) {
  return fmtSecondsAsHHMM(ms / 1000);
}

// with seconds — only used for the currently running timer clock
function fmtClockSec(ms) {
  const total = Math.floor(ms / 1000);
  const h = Math.floor(total / 3600);
  const m = Math.floor((total % 3600) / 60);
  const s = total % 60;
  return `${pad2(h)}:${pad2(m)}:${pad2(s)}`;
}

// (fmtDuration — "H:MM" without leading zero — lives in lib/format.js.)

// A balance that rounds to 0:00 is treated as exactly zero (±, neutral colour) so
// a stray few seconds never shows up as a green +0:00 or red −0:00.
function roundsToZero(ms) {
  return Math.round(Math.abs(ms) / 60000) === 0;
}
function signClass(ms) {
  return roundsToZero(ms) ? "is-zero" : ms > 0 ? "is-pos" : "is-neg";
}
function fmtSignedDuration(ms) {
  const totalMin = Math.round(Math.abs(ms) / 60000);
  const sign = totalMin === 0 ? "±" : ms < 0 ? "−" : "+";
  const h = Math.floor(totalMin / 60);
  const m = totalMin % 60;
  return `${sign}${h}:${pad2(m)} h`;
}

// H:MM without a leading zero on the hours, but single-digit hours are prefixed
// with a figure space (digit-width) so values stay the same width and line up.
function fmtClockH(ms) {
  const totalMin = Math.round(ms / 60000);
  const h = Math.floor(totalMin / 60);
  const hs = h < 10 ? ` ${h}` : String(h);
  return `${hs}:${pad2(totalMin % 60)}`;
}
// Signed +/- value split into sign + magnitude so the signs line up in a column
// while the numbers stay right-aligned (with a small gap between them).
function SignedDiff({ ms }) {
  return (
    <span className="ha-diff-inner">
      <span className="ha-diff-sign">{roundsToZero(ms) ? "±" : ms < 0 ? "−" : "+"}</span>
      <span className="ha-diff-mag">{fmtClockH(Math.abs(ms))}</span>
    </span>
  );
}

// (Workday/Soll, phase and credit helpers live in lib/timeMath.js.)

// (Synthetic credit entries and entitlement math live in lib/timeMath.js.)


// (Vacation balance, carry-over and hours-account balance live in lib/timeMath.js.)

// (Employment-phase scoping lives in lib/timeMath.js.)

export default function Time({ isAdmin }) {
  const [projects, setProjects] = useState([]);
  const [entries, setEntries] = useState([]);
  const [running, setRunning] = useState(null);
  // First-paint gate: stays false until the profile + first data load (entries,
  // vacations, holidays) have all completed. Latches true and never flips back,
  // so live WebSocket refreshes update in place instead of blanking the view.
  const [ready, setReady] = useState(false);
  const [holidaysLoaded, setHolidaysLoaded] = useState(false);
  const [error, setError] = useState(null);
  const [weekOffset, setWeekOffset] = useState(0);
  const [now, setNow] = useState(Date.now());

  const [showEntryForm, setShowEntryForm] = useState(false);
  const [editingEntry, setEditingEntry] = useState(null);
  const [entryForm, setEntryForm] = useState({ date: "", from: "", to: "", project_times: [] });
  const [formError, setFormError] = useState("");

  const [showVacations, setShowVacations] = useState(false);
  const [showHoursAccount, setShowHoursAccount] = useState(false);
  const [holidays, setHolidays] = useState({});
  const [vacations, setVacations] = useState([]);
  const [confirmDelete, setConfirmDelete] = useState(null); // entry pending deletion
  const [confirmDiscard, setConfirmDiscard] = useState(false); // running timer pending discard
  const [me, setMe] = useState(null);

  // Drop a pending discard once the timer is gone (stopped elsewhere) — otherwise
  // the dialog would silently reappear over the NEXT timer and delete that one.
  useEffect(() => { if (!running) setConfirmDiscard(false); }, [running]);

  const loadProjects = useCallback(async () => {
    try { setProjects(await fetchProjects()); } catch (e) { console.error(e); }
  }, []);

  // Zeit tab is self-scoped — even admins only see their own entries here.
  // We pass me.id explicitly, because otherwise the backend default for admins
  // returns ALL entries.
  const loadEntries = useCallback(async () => {
    if (me?.id == null) return;
    try {
      setEntries(await fetchTimeEntries(me.id));
      setError(null);
    } catch (e) {
      console.error(e);
      setError("Fehler beim Laden der Zeiteinträge");
    }
  }, [me?.id]);

  const loadTimer = useCallback(async () => {
    try { setRunning(await fetchRunningTimer()); } catch (e) { console.error(e); }
  }, []);

  const loadVacations = useCallback(async () => {
    // Always self-scoped: admins shouldn't accidentally see all employees' vacations here.
    if (me?.id == null) return; // wait until profile is loaded — otherwise admin would briefly receive all rows
    try { setVacations(await fetchVacations(me.id)); } catch (e) { console.error(e); }
  }, [me?.id]);
  useEffect(() => {
    // Wait for the profile before the first load — loadEntries/loadVacations are
    // self-scoped to me.id and would otherwise no-op, briefly showing empty data.
    if (me?.id == null) return;
    Promise.all([loadProjects(), loadEntries(), loadTimer(), loadVacations()])
      .finally(() => setReady(true));
  }, [me?.id, loadProjects, loadEntries, loadTimer, loadVacations]);

  // current user profile (for weekly_hours target)
  const loadMe = useCallback(async () => {
    try { setMe(await fetchMe()); } catch (e) { console.error(e); }
  }, []);
  useEffect(() => { loadMe(); }, [loadMe]);

  // First year that needs holiday data. The Stundenkonto sums the Soll from the
  // FIRST tracked Monday onward, so a fixed window (e.g. "current year − 2") would
  // silently drop the holiday credit of older years while their Soll keeps counting.
  // Derived from the data instead; /api/holidays/{year} computes any year.
  const firstHolidayYear = useMemo(() => {
    const cur = new Date().getFullYear();
    let min = cur;
    const take = (y) => { if (Number.isFinite(y) && y >= 1900 && y < min) min = y; };
    for (const p of me?.phases || []) take(Number(String(p.start_date || "").slice(0, 4)));
    if (me?.employed_since) take(Number(String(me.employed_since).slice(0, 4)));
    for (const e of entries) take(new Date(e.started_at).getFullYear());
    for (const v of vacations) take(Number(String(v.start_date).slice(0, 4)));
    return Math.max(min, cur - 25); // hard floor so bad data can't flood the API
  }, [me, entries, vacations]);

  // public holidays — same source as the calendar (/api/holidays/{year}).
  // Only the derived year range is a dependency, so this fires once per range change
  // and not on every entries/profile refresh.
  const meLoaded = me?.id != null;
  useEffect(() => {
    if (!meLoaded) return; // the window depends on the profile — wait for it
    // Latest-wins guard: when firstHolidayYear drops (older data arrives), the
    // wider fetch supersedes this one — without the flag a slower NARROW range
    // could resolve last and permanently drop the old years' holiday credit.
    let stale = false;
    const last = new Date().getFullYear() + 1;
    const years = [];
    for (let y = firstHolidayYear; y <= last; y++) years.push(y);
    Promise.all(years.map((yr) => fetchHolidays(yr).catch(() => ({}))))
      .then((results) => {
        if (stale) return;
        const merged = {};
        results.forEach((r) => Object.assign(merged, r));
        setHolidays(merged);
      })
      .catch(() => {})
      .finally(() => { if (!stale) setHolidaysLoaded(true); });
    return () => { stale = true; };
  }, [firstHolidayYear, meLoaded]);

  // tick the running clock every second (own timer or any visible "running" entry)
  const hasLiveEntry = running != null || entries.some((e) => !e.ended_at);
  useEffect(() => {
    if (!hasLiveEntry) return;
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, [hasLiveEntry]);

  // WebSocket for real-time updates
  useEffect(() => {
    let ws;
    let reconnectTimer;
    const connect = () => {
      ws = new WebSocket(getWsUrl());
      ws.onopen = () => {
        const token = getWsToken();
        if (token) ws.send(JSON.stringify({ token }));
      };
      ws.onmessage = (event) => {
        try {
          const msg = JSON.parse(event.data);
          if (msg.type === "workforce") { loadProjects(); loadEntries(); loadTimer(); loadVacations(); }
          if (msg.type === "users") { loadMe(); }
        } catch {}
      };
      // disposed guard: the cleanup's ws.close() fires onclose asynchronously —
      // without it a fresh reconnect timer was scheduled after unmount (zombie).
      ws.onclose = () => { if (!disposed) reconnectTimer = setTimeout(connect, 3000); };
      ws.onerror = () => { ws.close(); };
    };
    let disposed = false;
    connect();
    return () => { disposed = true; clearTimeout(reconnectTimer); if (ws) ws.close(); };
  }, [loadProjects, loadEntries, loadTimer, loadVacations, loadMe]);

  // --- derived: the visible week ---
  // Reactive: rolls over shortly after midnight / on tab focus, so a tab left
  // open overnight moves to the new day without a manual reload.
  const todayIso = useToday();
  const today = useMemo(() => parseISODateLocal(todayIso), [todayIso]);
  const periodStart = useMemo(() => addDays(getMonday(today), weekOffset * 7), [today, weekOffset]);
  const periodEnd = useMemo(() => addDays(periodStart, 7), [periodStart]);

  // Tags are ordered by colour (yellows, then greens, blues after) — see projectTagRank.
  // The original order breaks ties so equally-coloured projects stay stable.
  const activeProjects = useMemo(() => projects
    .filter((p) => !p.archived)
    .map((p, i) => ({ p, i, rank: projectTagRank(p) }))
    .sort((a, b) => a.rank - b.rank || a.i - b.i)
    .map((x) => x.p), [projects]);
  const projectById = useMemo(() => {
    const m = {};
    for (const p of projects) m[p.id] = p;
    return m;
  }, [projects]);
  // Gebucht wird nur auf die eigenen Projekte — auch als Admin: wer ein
  // weiteres braucht, traegt sich im Projekte-Tab dort ein. Ein Projekt ganz
  // ohne Zuordnung gilt als "fuer alle"; das entscheidet das Backend und
  // liefert es als `bookable` (fehlt das Feld, wird nicht gefiltert).
  // `activeProjects` bleibt die vollstaendige Liste, sonst fehlten die Namen
  // zu bereits gebuchten Zeiten.
  const trackableProjects = useMemo(
    () => activeProjects.filter((p) => p.bookable !== false),
    [activeProjects]
  );


  // Hours-account/credit calculation always runs for me (= logged-in user).
  const contextUser = me;

  // synthetic feiertag/urlaub "entries" — credit matches the contextUser's per-day
  // Soll. Built via the shared helpers so tile, modal and admin stay consistent.
  const holidayEntries = useMemo(
    () => buildHolidayEntries(contextUser, holidays),
    [holidays, contextUser]
  );

  const vacationEntries = useMemo(
    () => buildVacationEntries(contextUser, vacations, holidays),
    [vacations, holidays, contextUser]
  );

  const allEntries = useMemo(
    () => [...entries, ...holidayEntries, ...vacationEntries],
    [entries, holidayEntries, vacationEntries]
  );

  const periodEntries = useMemo(() => allEntries.filter((e) => {
    const s = new Date(e.started_at);
    return s >= periodStart && s < periodEnd;
  }), [allEntries, periodStart, periodEnd]);

  // group entries into days — only days that actually have entries
  const daysWithEntries = useMemo(() => {
    const map = {};
    for (const e of periodEntries) {
      const key = formatISODate(new Date(e.started_at));
      (map[key] = map[key] || []).push(e);
    }
    return Object.keys(map).sort().reverse().map((iso) => {
      const [y, m, d] = iso.split("-").map(Number);
      const date = new Date(y, m - 1, d);
      const dayEntries = map[iso].sort((a, b) => new Date(a.started_at) - new Date(b.started_at));
      return {
        iso,
        date,
        weekdayIdx: (date.getDay() + 6) % 7,
        isToday: date.getTime() === today.getTime(),
        entries: dayEntries,
        sumMs: dayEntries.reduce((s, e) => s + entryDurationMs(e, now), 0),
      };
    });
  }, [periodEntries, today, now]);

  const periodTotalMs = useMemo(
    () => periodEntries.reduce((sum, e) => sum + entryDurationMs(e, now), 0),
    [periodEntries, now]
  );

  // per-day buckets for the bar chart, split by entry type
  const chartDays = useMemo(() => {
    const buckets = [];
    let cur = new Date(periodStart);
    while (cur < periodEnd) {
      buckets.push({
        date: new Date(cur),
        iso: formatISODate(cur),
        ms: 0,
        projectMs: 0,
        byKind: { work: 0, vacation: 0, sick: 0, holiday: 0 },
        isToday: cur.getTime() === today.getTime(),
        weekdayIdx: (cur.getDay() + 6) % 7,
      });
      cur = addDays(cur, 1);
    }
    const byIso = {};
    buckets.forEach((b, i) => { byIso[b.iso] = i; });
    for (const e of periodEntries) {
      const iso = formatISODate(new Date(e.started_at));
      const idx = byIso[iso];
      if (idx !== undefined) {
        const d = entryDurationMs(e, now);
        buckets[idx].ms += d;
        buckets[idx].byKind[kindOf(e)] += d;
        for (const pt of e.project_times || []) {
          buckets[idx].projectMs += (pt.seconds || 0) * 1000;
        }
      }
    }
    // Projektzeit ist Teil der Arbeitszeit und kann sie nie übersteigen
    // (Rundung/Altdaten) — sonst würde der Balken über 100 % laufen.
    for (const b of buckets) b.projectMs = Math.min(b.projectMs, b.byKind.work);
    return buckets;
  }, [periodStart, periodEnd, periodEntries, today, now]);

  // y-axis: always at least 0–8 h; taller only if a day exceeds 8 h
  const axisMaxMs = useMemo(() => {
    const realMax = Math.max(0, ...chartDays.map((d) => d.ms));
    const EIGHT_H = 8 * 3600000;
    if (realMax <= EIGHT_H) return EIGHT_H;
    return Math.ceil(realMax / 3600000) * 3600000; // round up to next full hour
  }, [chartDays]);

  const chartScaleMs = axisMaxMs / 0.88; // headroom above the top gridline for the value label

  const gridTicks = useMemo(() => {
    const N = 4;
    return Array.from({ length: N }, (_, i) => {
      const k = i + 1;
      const hours = (axisMaxMs * k) / N / 3600000;
      const label = Number.isInteger(hours) ? `${hours}h` : `${hours.toFixed(1)}h`;
      return { k, pct: (k / N) * 88, label };
    });
  }, [axisMaxMs]);

  // BOTH accounts are judged by the ACTIVE employment phase only — nothing
  // carries across a phase change, and the admin detail view scopes the same
  // way, so both views always show the same numbers for the same person.
  const myPhase = useMemo(() => activePhaseOf(me, today), [me, today]);
  const phaseUser = useMemo(() => (myPhase ? scopeUserToPhase(me, myPhase) : me), [me, myPhase]);
  const phaseEntries = useMemo(() => (myPhase ? scopeEntriesToPhase(entries, myPhase) : entries), [entries, myPhase]);
  const phaseVacations = useMemo(() => (myPhase ? clipVacationsToPhase(vacations, myPhase) : vacations), [vacations, myPhase]);
  const phaseHolidayEntries = useMemo(() => buildHolidayEntries(phaseUser, holidays), [phaseUser, holidays]);
  const phaseVacationEntries = useMemo(
    () => buildVacationEntries(phaseUser, phaseVacations, holidays),
    [phaseUser, phaseVacations, holidays]
  );

  const weeklyHoursTarget = phaseUser?.weekly_hours ?? 40;

  // Hours account: plus/minus over all completed weeks (excluding the current one).
  // Canonical calculation (per-day target) — modal and admin overview use the
  // same function so the numbers match everywhere.
  // Minute-granular "now": the balance excludes the running week, so seconds
  // never change the result — but with the raw ticking `now` as dep this memo
  // re-scanned every entry once per second while a timer ran.
  const nowMinute = Math.floor(now / 60000) * 60000;
  const accountBalance = useMemo(
    () => accountBalanceMs(phaseUser, phaseEntries, phaseHolidayEntries, phaseVacationEntries, today, nowMinute),
    [phaseUser, phaseEntries, phaseHolidayEntries, phaseVacationEntries, today, nowMinute]
  );

  const vacationAccountFor = useCallback(
    (year) => vacationAccountForPhase(me, myPhase, entries, vacations, holidays, year, today),
    [me, myPhase, entries, vacations, holidays, today]
  );

  // Open vacation days this year via the canonical balance — includes the auto
  // carry-over from the previous year (consumed first); counts non-rejected requests.
  const vacationBal = useMemo(
    () => vacationAccountFor(today.getFullYear()).balance,
    [vacationAccountFor, today]
  );
  // "Offen" ist NUR der Jahresanspruch. Der Übertrag hat eine eigene Frist
  // (31.03.) und steht als eigener Wert daneben — vermischt führte die Zahl in
  // die Irre (er kann für Urlaub nach dem 31.03. gar nicht mehr genutzt werden).
  const vacationOpen = vacationBal.annualRemaining;
  const vacationDenom = vacationBal.entitlement;
  const vacationCarry = vacationBal.carryRemaining;

  // --- timer actions ---
  const handleStart = async () => {
    try {
      await startTimer("");
      await Promise.all([loadTimer(), loadEntries()]);
    } catch (e) { setError(e.message); }
  };

  // Start the timer and immediately activate the clicked project.
  const handleStartOnProject = async (projectId) => {
    try {
      await startTimer("");
      await toggleTimerProject(projectId);
      await Promise.all([loadTimer(), loadEntries()]);
    } catch (e) { setError(e.message); }
  };

  const handleStop = async () => {
    try {
      await stopTimer();
      await Promise.all([loadTimer(), loadEntries()]);
    } catch (e) { setError(e.message); }
  };

  // Throw the running entry away instead of saving it (mis-started timer). The
  // entry only exists while running, so deleting it leaves no trace.
  const handleDiscard = async () => {
    setConfirmDiscard(false);
    if (!running?.id) return;
    try {
      // The timer may have been stopped elsewhere (other device) since the dialog
      // opened — deleting then would throw away real, already-saved work.
      const current = await fetchRunningTimer();
      if (!current || current.id !== running.id) {
        await Promise.all([loadTimer(), loadEntries()]);
        setError("Der Timer wurde zwischenzeitlich gestoppt — es wurde nichts verworfen.");
        return;
      }
      await deleteTimeEntry(running.id);
      await Promise.all([loadTimer(), loadEntries()]);
    } catch (e) { setError(e.message); }
  };

  const handleSetProjectTimes = async (entry, projectTimes) => {
    try {
      await setEntryProjectTimes(entry.id, projectTimes);
      await loadEntries();
    } catch (e) {
      setError(e.message);
      loadEntries();
    }
  };

  const handleToggleProject = async (projectId) => {
    try {
      await toggleTimerProject(projectId);
      await Promise.all([loadTimer(), loadEntries()]);
    } catch (e) { setError(e.message); }
  };

  // inline edit of an entry's start/end time
  const handleSaveTimes = async (entry, vonStr, bisStr, dateOverride) => {
    const dateStr = dateOverride || dateInputFromISO(entry.started_at);
    const started_at = combineDateTime(dateStr, vonStr);
    if (!started_at) { setError("Bitte eine gueltige Zeit eingeben (HH:MM)"); loadEntries(); return; }
    // Keep the entry's original day span: for a timer that ran past midnight
    // (Mo 22:00 – Di 01:30) the end stays on the following day — recombining
    // both times onto the start date made such entries impossible to edit.
    let ended_at = null;
    if (entry.ended_at) {
      let spanDays = Math.round(
        (parseISODateLocal(dateInputFromISO(entry.ended_at)) - parseISODateLocal(dateInputFromISO(entry.started_at))) / 86400000
      );
      let endDateStr = formatISODate(addDays(parseISODateLocal(dateStr), spanDays));
      ended_at = combineDateTime(endDateStr, bisStr);
      if (!ended_at) { setError("Bitte eine gueltige Zeit eingeben (HH:MM)"); loadEntries(); return; }
      // Keeping the day span can overshoot: Mo 22:00–Di 00:00 with "Bis" edited
      // to 23:30 would end Di 23:30 = a 25.5h entry. A shift never lasts a full
      // day, so pull the end back while the duration reaches 24h.
      while (spanDays > 0 && new Date(ended_at) - new Date(started_at) >= 86400000) {
        spanDays -= 1;
        endDateStr = formatISODate(addDays(parseISODateLocal(dateStr), spanDays));
        ended_at = combineDateTime(endDateStr, bisStr);
      }
    }
    if (ended_at && new Date(ended_at) <= new Date(started_at)) {
      setError("Bis muss nach Von liegen");
      loadEntries();
      return;
    }
    try {
      await updateTimeEntry(entry.id, { started_at, ended_at, version: entry.version });
      await Promise.all([loadEntries(), loadTimer()]);
    } catch (e) {
      setError(e.message);
      loadEntries();
    }
  };

  // --- entry form ---
  const openNewEntry = () => {
    const base = weekOffset === 0 ? today : periodStart;
    setEditingEntry(null);
    setEntryForm({ date: formatISODate(base), from: "09:00", to: "17:00", project_times: [] });
    setFormError("");
    setShowEntryForm(true);
  };

  const openEditEntry = (entry) => {
    setEditingEntry(entry);
    setEntryForm({
      date: dateInputFromISO(entry.started_at),
      from: timeInputFromISO(entry.started_at),
      to: entry.ended_at ? timeInputFromISO(entry.ended_at) : "",
      project_times: (entry.project_times || []).filter((pt) => pt.seconds > 0).map((pt) => ({ project_id: pt.project_id, seconds: pt.seconds })),
    });
    setFormError("");
    setShowEntryForm(true);
  };

  const submitEntry = async (ev) => {
    ev.preventDefault();
    if (!entryForm.date || !entryForm.from) { setFormError("Datum und Von-Zeit sind erforderlich"); return; }
    const fromNorm = parseTimeDigits(entryForm.from);
    const toNorm = entryForm.to ? parseTimeDigits(entryForm.to) : null;
    if (!fromNorm || (entryForm.to && !toNorm)) { setFormError("Bitte eine gueltige Zeit eingeben (HH:MM)"); return; }
    const started_at = combineDateTime(entryForm.date, fromNorm);
    let ended_at = toNorm ? combineDateTime(entryForm.date, toNorm) : null;
    if (!started_at || (toNorm && !ended_at)) { setFormError("Bitte ein gueltiges Datum eingeben"); return; }
    if (ended_at && new Date(ended_at) <= new Date(started_at)) {
      // Same rule as the inline editor: an entry that originally ran past
      // midnight keeps its day span — otherwise a Mo 22:00–Di 01:30 timer
      // was impossible to edit here ("Bis muss nach Von liegen").
      const origSpan = editingEntry?.ended_at
        ? Math.round((parseISODateLocal(dateInputFromISO(editingEntry.ended_at)) - parseISODateLocal(dateInputFromISO(editingEntry.started_at))) / 86400000)
        : 0;
      if (origSpan > 0) {
        ended_at = combineDateTime(formatISODate(addDays(parseISODateLocal(entryForm.date), origSpan)), toNorm);
      }
    }
    if (ended_at && new Date(ended_at) <= new Date(started_at)) { setFormError("Bis muss nach Von liegen"); return; }
    const totalSec = ended_at ? Math.max(0, (new Date(ended_at) - new Date(started_at)) / 1000) : 0;
    const pts = (entryForm.project_times || []).filter((pt) => pt.project_id && pt.seconds > 0);
    const sumPts = pts.reduce((s, pt) => s + pt.seconds, 0);
    if (sumPts > totalSec + 1) { setFormError("Projekt-Zeiten überschreiten die Gesamtzeit"); return; }
    const payload = {
      started_at,
      ended_at,
      description: "",
      // Editing must keep the entry's kind — hard-coding "work" silently turned
      // an admin-edited sick entry into a work entry (wrong sick statistics).
      kind: editingEntry?.kind ?? "work",
    };
    try {
      let created;
      if (editingEntry) {
        await updateTimeEntry(editingEntry.id, { ...payload, version: editingEntry.version });
      } else {
        created = await createTimeEntry(payload);
      }
      const targetId = editingEntry ? editingEntry.id : created?.id;
      if (targetId) {
        try {
          await setEntryProjectTimes(targetId, pts.map((pt) => ({ project_id: Number(pt.project_id), seconds: pt.seconds })));
        } catch (e) { /* entry persists even if pt update fails */ }
      }
      setShowEntryForm(false);
      setEditingEntry(null);
      await Promise.all([loadEntries(), loadTimer()]);
    } catch (e) {
      if (e.status === 409) { setFormError(e.message); await loadEntries(); }
      else setFormError(e.message || "Fehler beim Speichern");
    }
  };

  const handleDeleteEntry = (e) => setConfirmDelete(e);

  const handleDuplicateEntry = async (entry) => {
    try {
      const created = await createTimeEntry({
        started_at: entry.started_at,
        ended_at: entry.ended_at,
        description: entry.description || "",
        kind: entry.kind || "work",
      });
      const pts = (entry.project_times || []).filter((pt) => pt.seconds > 0);
      if (created?.id && pts.length > 0) {
        try { await setEntryProjectTimes(created.id, pts.map((pt) => ({ project_id: pt.project_id, seconds: pt.seconds }))); }
        catch {}
      }
      await Promise.all([loadEntries(), loadTimer()]);
    } catch (err) { setError(err.message); }
  };

  const doDeleteEntry = async () => {
    const e = confirmDelete;
    setConfirmDelete(null);
    if (!e) return;
    try {
      await deleteTimeEntry(e.id);
      await Promise.all([loadEntries(), loadTimer()]);
    } catch (err) { setError(err.message); }
  };

  // First-paint gate: don't render the view (Stundenkonto, vacation balance …)
  // until profile + entries + vacations + holidays are all in — otherwise the
  // numbers would render empty and then jump once the data arrives.
  if (!ready || !holidaysLoaded) {
    return (
      <div
        className="time-view"
        style={{ display: "flex", alignItems: "center", justifyContent: "center", minHeight: "60vh", color: "var(--text-muted)" }}
      >
        Laden…
      </div>
    );
  }

  return (
    <div className="time-view">
      {error && (
        <div className="app-error-bar">
          {error}
          <button onClick={() => setError(null)}>&times;</button>
        </div>
      )}

      {/* Timer card */}
      <div className="wf-timer-card">
        <div className="wf-accounts">
          <button type="button" className="wf-account wf-account-btn" onClick={() => setShowVacations(true)}>
            <span className="wf-account-label">Urlaubskonto</span>
            <span className="wf-account-value is-vacation">
              {vacationOpen}<span className="wf-account-sep wf-account-sep-slash">/</span>{vacationDenom}
              {vacationCarry > 0 && (
                <span
                  className="wf-account-carry"
                  title={`Zusätzlich ${vacationCarry} Tage Übertrag aus dem Vorjahr — verfällt ${fmtDateDE(vacationBal.expiresOn)}`}
                >+{vacationCarry}</span>
              )}
              <span className="wf-account-unit">Offen</span>
            </span>
          </button>
          <button type="button" className="wf-account wf-account-btn" onClick={() => setShowHoursAccount(true)}>
            <span className="wf-account-label">Stundenkonto</span>
            <span className={`wf-account-value ${signClass(accountBalance)}`}>
              {(() => {
                const ms = accountBalance;
                const sign = ms < 0 ? "−" : "+";
                const totalMin = Math.round(Math.abs(ms) / 60000);
                const h = Math.floor(totalMin / 60);
                const m = totalMin % 60;
                return (
                  <>
                    <span className="wf-account-sep">{sign}</span>
                    {h}:{pad2(m)}
                  </>
                );
              })()}
              <span className="wf-account-unit">h</span>
            </span>
          </button>
          <button type="button" className="wf-account wf-account-btn wf-account-add" onClick={openNewEntry} title="Neuer Eintrag">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <line x1="12" y1="5" x2="12" y2="19" /><line x1="5" y1="12" x2="19" y2="12" />
            </svg>
          </button>
        </div>
        <div className="wf-timer-side">
          {running ? (
            <>
              {trackableProjects.length > 0 && (
                <div className="wf-timer-tags">
                  {trackableProjects.map((p) => {
                    const on = (running.active_project_ids || []).includes(p.id);
                    const col = p.client_color || p.color;
                    const code = (p.abbreviation || "").replace(/\d+$/, "");
                    const label = code ? `${code} - ${p.name}` : p.name;
                    const tip = p.client ? `${p.client} – ${p.name}` : p.name;
                    return (
                      <button
                        key={p.id}
                        type="button"
                        className={`wf-proj-tag ${on ? "is-on" : ""}`}
                        style={on
                          ? { background: col, borderColor: col, color: "#fff" }
                          : { borderColor: col, color: col }}
                        onClick={() => handleToggleProject(p.id)}
                        title={tip}
                      >
                        <svg className="wf-proj-tag-ico" width="9" height="9" viewBox="0 0 24 24" fill="currentColor" stroke="none"><polygon points="7 5 19 12 7 19" /></svg>
                        <span className="wf-proj-tag-label">{label}</span>
                      </button>
                    );
                  })}
                </div>
              )}
              <div className="wf-timer-clock-col">
                <span className="wf-timer-clock-running">{fmtClockSec(entryDurationMs(running, now))}</span>
                <button
                  type="button"
                  className="wf-timer-btn-discard"
                  onClick={() => setConfirmDiscard(true)}
                  title="Laufende Zeit verwerfen"
                  aria-label="Laufende Zeit verwerfen"
                >
                  <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
                    <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
                  </svg>
                </button>
              </div>
              <button className="wf-timer-btn wf-timer-btn-stop" onClick={handleStop} title="Stopp">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor" stroke="none"><rect x="6" y="6" width="12" height="12" rx="1.5" /></svg>
              </button>
            </>
          ) : (
            <>
              {trackableProjects.length > 0 && (
                <div className="wf-timer-tags">
                  {trackableProjects.map((p) => {
                    const col = p.client_color || p.color;
                    const code = (p.abbreviation || "").replace(/\d+$/, "");
                    const label = code ? `${code} - ${p.name}` : p.name;
                    const tip = p.client ? `${p.client} – ${p.name}` : p.name;
                    return (
                      <button
                        key={p.id}
                        type="button"
                        className="wf-proj-tag"
                        style={{ borderColor: col, color: col }}
                        onClick={() => handleStartOnProject(p.id)}
                        title={tip}
                      >
                        <svg className="wf-proj-tag-ico" width="9" height="9" viewBox="0 0 24 24" fill="currentColor" stroke="none"><polygon points="7 5 19 12 7 19" /></svg>
                        <span className="wf-proj-tag-label">{label}</span>
                      </button>
                    );
                  })}
                </div>
              )}
              {/* Invisible clock+discard column so the row doesn't shift when the
                  running state appears. The clock text must mirror fmtClockSec's
                  widest shape ("00:00:00", 8 digits). */}
              <div className="wf-timer-clock-col wf-timer-clock-col-placeholder" aria-hidden="true">
                <span className="wf-timer-clock-running">00:00:00</span>
                <span className="wf-timer-btn-discard" />
              </div>
              <button className="wf-timer-btn wf-timer-btn-start" onClick={handleStart} title="Start (ohne Projekt)">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor" stroke="none"><polygon points="7 5 19 12 7 19" /></svg>
              </button>
            </>
          )}
        </div>
      </div>

      {/* Weekly bar chart */}
      <div className="wf-chart-card">
        <div className="wf-chart-total">
          <div className="wf-chart-nav">
            <button
              className="wf-nav-btn"
              onClick={() => setWeekOffset((o) => o - 1)}
              title="Vorherige Woche"
            >
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="15 18 9 12 15 6" /></svg>
            </button>
            <span className="wf-nav-today" onClick={() => setWeekOffset(0)}>Heute</span>
            <WeekPicker
              periodStart={periodStart}
              onPick={(d) => {
                const target = getMonday(d);
                const todayMon = getMonday(today);
                const diffDays = Math.round((target - todayMon) / 86400000);
                setWeekOffset(Math.round(diffDays / 7));
              }}
            />
            <button
              className="wf-nav-btn"
              onClick={() => setWeekOffset((o) => o + 1)}
              title="Nächste Woche"
            >
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="9 18 15 12 9 6" /></svg>
            </button>
          </div>
          <div className="wf-chart-legend wf-chart-legend-inline">
            {KIND_ORDER.flatMap((k) => {
              const items = [];
              if (chartDays.some((d) => d.byKind[k] > 0)) {
                items.push({ key: k, color: KINDS[k].color, label: KINDS[k].label });
              }
              // "Projekt" direkt hinter "Arbeit" — es ist dessen Teilmenge.
              if (k === "work" && chartDays.some((d) => d.projectMs > 0)) {
                items.push({ key: "project", color: PROJECT_COLOR, label: "Projekt" });
              }
              return items;
            }).map((it) => (
              <span key={it.key} className="wf-legend-item">
                <span className="wf-legend-dot" style={{ background: it.color }} />
                {it.label}
              </span>
            ))}
          </div>
          <div className="wf-chart-total-main">
            <span className="wf-chart-total-label">Gesamt</span>
            <span className="wf-chart-total-value">
              {Math.floor(Math.round(periodTotalMs / 60000) / 60).toLocaleString("de-DE")}
              <span className="wf-total-min">:{pad2(Math.round(periodTotalMs / 60000) % 60)}</span>
            </span>
          </div>
        </div>
        <div className="wf-chart">
          <div className="wf-chart-grid">
            {gridTicks.map((t) => (
              <div key={t.k} className="wf-chart-gridline" style={{ bottom: `${t.pct}%` }}>
                <span className="wf-chart-tick-label">{t.label}</span>
              </div>
            ))}
          </div>
          <div className="wf-chart-cols">
            {chartDays.map((d) => {
              const h = chartScaleMs > 0 ? (d.ms / chartScaleMs) * 100 : 0;
              // Weekday and date live in their own spans so the mobile CSS can
              // stack them ("Mo" / "3. Feb"); on desktop they render as one line.
              const wdLabel = WEEKDAYS[d.weekdayIdx];
              const domLabel = `${d.date.getDate()}.`;
              const monLabel = MONTHS_SHORT[d.date.getMonth()];
              return (
                <div key={d.iso} className="wf-chart-col">
                  <div className="wf-chart-bar-zone">
                    {d.ms > 0 && <span className="wf-chart-val">{fmtClock(d.ms)}</span>}
                    <div className="wf-chart-bar" style={{ height: `${d.ms > 0 ? Math.max(h, 1.5) : 0}%` }}>
                      {barSegments(d.byKind, d.projectMs, d.ms).map((s) => (
                        <div key={s.key} className="wf-chart-seg" style={{ height: `${s.pct}%`, background: s.color }} />
                      ))}
                    </div>
                  </div>
                  <span className={`wf-chart-day ${d.isToday ? "is-today" : ""}`}><span className="wf-chart-day-wd">{wdLabel}</span><span className="wf-chart-day-sep">,</span> <span className="wf-chart-day-date"><span className="wf-chart-day-dom">{domLabel}</span> <span className="wf-chart-day-mon">{monLabel}</span></span></span>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* No in-page "Laden…" bar: the first load is covered by the full-page
          gate above, and later refreshes update in place — a text bar on top of
          fully visible content just looked broken. */}

      {/* Entry list — only days with tracked time */}
      <div className="wf-days">
        {daysWithEntries.length === 0 && (
          <div className="wf-empty-week">Keine Zeiteinträge in dieser Woche.</div>
        )}
        {daysWithEntries.map((day) => (
          <div key={day.iso} className="wf-day">
            <div className="wf-day-header">
              <span className="wf-day-name">
                {WEEKDAYS_LONG[day.weekdayIdx]}<span className="wf-day-date">{day.date.getDate()}. {MONTHS_SHORT[day.date.getMonth()]}</span>
              </span>
              <span className="wf-day-sum">{fmtDuration(day.sumMs)}</span>
            </div>
            {day.entries.map((e) => (
              <EntryRow
                key={e.id}
                entry={e}
                now={now}
                projectById={projectById}
                canManageSick={isAdmin}
                onSaveTimes={handleSaveTimes}
                onSetProjectTimes={handleSetProjectTimes}
                onDelete={handleDeleteEntry}
                onDuplicate={handleDuplicateEntry}
                onEdit={openEditEntry}
              />
            ))}
          </div>
        ))}
      </div>

      {/* Entry form modal */}
      {showEntryForm && (
        <div className="modal-overlay" onClick={() => { setShowEntryForm(false); setEditingEntry(null); }}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2 className="modal-title">{editingEntry ? "Eintrag bearbeiten" : "Neuer Eintrag"}</h2>
              <button className="modal-close-btn" onClick={() => { setShowEntryForm(false); setEditingEntry(null); }}>&times;</button>
            </div>
            <form onSubmit={submitEntry} className="wf-form">
              {(() => {
                // Waehrend des Tippens steht in from/to auch mal "1" oder "12:6".
                // parseTimeDigits liefert dann null und es wird gar nicht
                // gerechnet — vorher warf das "Invalid time value" und riss die
                // ganze Zeiterfassung mit.
                const fromT = parseTimeDigits(entryForm.from);
                const toT = parseTimeDigits(entryForm.to);
                const formDate = entryForm.date || formatISODate(today);
                const totalSec = fromT && toT
                  ? Math.max(0, (new Date(combineDateTime(formDate, toT)) - new Date(combineDateTime(formDate, fromT))) / 1000)
                  : 0;
                return (
                  <>
                    <div className="wf-field-row wf-field-row-date">
                      <label className="wf-field wf-field-date">
                        <span>Datum</span>
                        <DatePicker value={entryForm.date} onChange={(d) => setEntryForm((f) => ({ ...f, date: d }))} clearable={false} shortYear />
                      </label>
                      <div className="wf-form-hours-display">{totalSec > 0 ? fmtSecondsAsHHMM(totalSec) : "—"} H</div>
                    </div>
                    <div className="wf-field-row">
                      <label className="wf-field">
                        <span>Von</span>
                        <TimeDropdown value={entryForm.from} onChange={(v) => setEntryForm((f) => ({ ...f, from: v }))} placeholder="HH:MM" />
                      </label>
                      <label className="wf-field">
                        <span>Bis</span>
                        <TimeDropdown value={entryForm.to} onChange={(v) => setEntryForm((f) => ({ ...f, to: v }))} placeholder="HH:MM" />
                      </label>
                    </div>

                    <div className="wf-field wf-form-projects">
                      <span className="wf-form-projects-label">davon Projekte</span>
                      <div className="wf-form-pt-tags">
                        {trackableProjects.map((p) => {
                          const on = (entryForm.project_times || []).some((pt) => pt.project_id === p.id);
                          const col = p.client_color || p.color;
                    const code = (p.abbreviation || "").replace(/\d+$/, "");
                          const lbl = code ? `${code} - ${p.name}` : p.name;
                          return (
                            <button
                              key={p.id}
                              type="button"
                              className={`wf-proj-tag ${on ? "is-on" : ""}`}
                              style={on ? { background: col, borderColor: col, color: "#fff" } : { borderColor: col, color: col }}
                              title={p.client ? `${p.client} – ${p.name}` : p.name}
                              onClick={() => setEntryForm((f) => {
                                const list = f.project_times || [];
                                const idx = list.findIndex((pt) => pt.project_id === p.id);
                                if (idx >= 0) return { ...f, project_times: list.filter((_, k) => k !== idx) };
                                const alloc = list.reduce((s, pt) => s + (pt.seconds || 0), 0);
                                const rem = Math.max(0, totalSec - alloc);
                                return { ...f, project_times: [...list, { project_id: p.id, seconds: rem }] };
                              })}
                            >
                              <span className="wf-proj-tag-label">{lbl}</span>
                            </button>
                          );
                        })}
                      </div>
                      {(entryForm.project_times || []).length > 0 && (
                        <div className="wf-form-pt-list">
                          {(entryForm.project_times || []).map((pt, i) => {
                            const proj = projectById[pt.project_id];
                            return (
                              <div key={i} className="wf-form-pt-row">
                                {/* Same colour source as the tag above (client colour wins over
                                    the project's own), otherwise the name shows a different hue. */}
                                <span className="wf-form-pt-name" style={{ color: proj ? (proj.client_color || proj.color) : undefined }}>{proj ? proj.name : "—"}</span>
                                <PtTimeInput
                                  seconds={pt.seconds || 0}
                                  onChange={(secs) => setEntryForm((f) => {
                                    const next = [...(f.project_times || [])];
                                    next[i] = { ...next[i], seconds: secs };
                                    return { ...f, project_times: next };
                                  })}
                                />
                                <button
                                  type="button"
                                  className="wf-icon-btn wf-icon-danger"
                                  title="Entfernen"
                                  onClick={() => setEntryForm((f) => ({ ...f, project_times: f.project_times.filter((_, idx) => idx !== i) }))}
                                >×</button>
                              </div>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  </>
                );
              })()}
              {formError && <div className="wf-form-error">{formError}</div>}
              <div className="wf-form-actions">
                {editingEntry && (
                  <button type="button" className="btn btn-danger" onClick={async () => { await handleDeleteEntry(editingEntry); setShowEntryForm(false); setEditingEntry(null); }}>Löschen</button>
                )}
                <div className="wf-form-actions-right">
                  <button type="submit" className="btn btn-primary">{editingEntry ? "Speichern" : "Erstellen"}</button>
                </div>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Vacation requests modal */}
      {showVacations && (
        <VacationModal
          vacations={vacations}
          holidays={holidays}
          accountFor={vacationAccountFor}
          onClose={() => setShowVacations(false)}
          onChanged={loadVacations}
        />
      )}

      {/* Stundenkonto modal */}
      {showHoursAccount && (
        <HoursAccountModal
          entries={phaseEntries}
          holidayEntries={phaseHolidayEntries}
          vacationEntries={phaseVacationEntries}
          weeklyHoursTarget={weeklyHoursTarget}
          today={today}
          user={phaseUser}
          onClose={() => setShowHoursAccount(false)}
        />
      )}

      {/* Delete confirmation */}
      {confirmDelete && (
        <ConfirmDialog
          message="Diesen Zeiteintrag wirklich löschen?"
          onConfirm={doDeleteEntry}
          onCancel={() => setConfirmDelete(null)}
        />
      )}

      {confirmDiscard && running && (
        <ConfirmDialog
          message={`Laufende Zeit (${fmtClockSec(entryDurationMs(running, now))}) verwerfen? Sie wird nicht gespeichert.`}
          confirmLabel="Verwerfen"
          onConfirm={handleDiscard}
          onCancel={() => setConfirmDiscard(false)}
        />
      )}
    </div>
  );
}

function EntryRow({ entry, now, projectById = {}, canManageSick = true, onSaveTimes, onSetProjectTimes, onDelete, onDuplicate, onEdit }) {
  const isRunning = !entry.ended_at;
  const k = kindOf(entry);
  // Holidays & approved vacation are synthetic and never editable. Sick entries
  // are admin territory (the backend enforces the same rule) — employees see
  // them read-only exactly like the synthetic credits.
  const locked = !!entry.synthetic || (k === "sick" && !canManageSick);
  const origFrom = timeInputFromISO(entry.started_at);
  const origTo = entry.ended_at ? timeInputFromISO(entry.ended_at) : "";
  const origDate = dateInputFromISO(entry.started_at);
  const [from, setFrom] = useState(origFrom);
  const [to, setTo] = useState(origTo);
  const [date, setDate] = useState(origDate);
  const [expanded, setExpanded] = useState(false);

  useEffect(() => {
    setFrom(origFrom);
    setTo(origTo);
    setDate(origDate);
  }, [origFrom, origTo, origDate]);

  const commit = (which, raw) => {
    if (locked) return;
    const norm = parseTimeDigits(raw);
    if (which === "from") {
      if (!norm) { setFrom(origFrom); return; }
      setFrom(norm);
      if (norm !== origFrom) onSaveTimes(entry, norm, to || origTo, date);
    } else {
      if (!norm) { setTo(origTo); return; }
      setTo(norm);
      if (norm !== origTo) onSaveTimes(entry, from || origFrom, norm, date);
    }
  };

  const commitDate = (raw) => {
    if (locked || !raw) { setDate(origDate); return; }
    setDate(raw);
    if (raw !== origDate) onSaveTimes(entry, from || origFrom, to || origTo, raw);
  };

  // holidays show their actual name; everything else shows the kind label
  const title = k === "holiday" && entry.description ? entry.description : KINDS[k].label;

  const projectTimes = [...(entry.project_times || [])]
    .filter((pt) => pt.seconds > 0)
    .sort((a, b) => b.seconds - a.seconds);
  const hasSub = projectTimes.length > 0;
  const showExpanded = expanded && hasSub;

  const commitProjectTime = (projectId, raw) => {
    if (isRunning || locked) return false; // locked: sick entries are admin-only
    const newSecs = parseDurationToSeconds(raw);
    if (newSecs == null) return false;
    const totalEntry = entryDurationMs(entry, now) / 1000;
    const others = projectTimes.filter((pt) => pt.project_id !== projectId);
    const sumOthers = others.reduce((s, pt) => s + pt.seconds, 0);
    if (sumOthers + newSecs > totalEntry + 1) return false; // exceeds entry duration
    const next = projectTimes.map((pt) =>
      pt.project_id === projectId ? { project_id: pt.project_id, seconds: newSecs } : { project_id: pt.project_id, seconds: pt.seconds }
    );
    onSetProjectTimes(entry, next);
    return true;
  };

  return (
    <>
    <div className={`wf-entry ${isRunning ? "wf-entry-running" : ""} ${showExpanded ? "wf-entry-has-sub" : ""}`}>
      {hasSub ? (
        <button
          type="button"
          className={`wf-entry-expand ${showExpanded ? "is-open" : ""}`}
          onClick={() => setExpanded((x) => !x)}
          title={showExpanded ? "Aufklappen schließen" : "Aufschlüsselung anzeigen"}
        >
          <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"><polyline points="9 18 15 12 9 6" /></svg>
        </button>
      ) : (
        <span className="wf-entry-expand-placeholder" />
      )}
      <span className="wf-entry-title">{title}</span>
      {!locked ? (
        <span className="wf-entry-date">
          <DatePicker value={date} onChange={commitDate} clearable={false} shortYear iconOnly />
        </span>
      ) : (
        <span className="wf-entry-date-placeholder" />
      )}
      <span className="wf-entry-times">
        <input
          className="wf-time-input"
          value={from}
          maxLength={5}
          disabled={locked}
          onChange={(e) => setFrom(e.target.value)}
          onBlur={(e) => commit("from", e.target.value)}
          onKeyDown={(e) => { if (e.key === "Enter") e.target.blur(); }}
        />
        <span className="wf-time-sep">–</span>
        {isRunning ? (
          <span className="wf-time-running">…</span>
        ) : (
          <input
            className="wf-time-input"
            value={to}
            maxLength={5}
            disabled={locked}
            onChange={(e) => setTo(e.target.value)}
            onBlur={(e) => commit("to", e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter") e.target.blur(); }}
          />
        )}
      </span>
      <span className="wf-entry-fill" />
      {!locked && !isRunning && onEdit && (
        <button className="wf-icon-btn" title="Bearbeiten" onClick={() => onEdit(entry)}>
          <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M4 20h4l10-10-4-4L4 16v4z" />
          </svg>
        </button>
      )}
      {!locked && !isRunning && onDuplicate && (
        <button className="wf-icon-btn" title="Duplizieren" onClick={() => onDuplicate(entry)}>
          <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <rect x="9" y="9" width="13" height="13" rx="2" ry="2" />
            <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" />
          </svg>
        </button>
      )}
      {!locked && (
        <button className="wf-icon-btn wf-icon-danger" title="Löschen" onClick={() => onDelete(entry)}>×</button>
      )}
      <span className="wf-entry-dur">
        {isRunning
          ? <span className="wf-running-tag">{fmtClock(entryDurationMs(entry, now))}</span>
          : fmtDuration(entryDurationMs(entry, now))}
      </span>
    </div>
    {showExpanded && (
      <div className="wf-entry-projects">
        {projectTimes.map((pt) => (
          <ProjectBreakdownRow
            key={pt.project_id}
            project={projectById[pt.project_id]}
            seconds={pt.seconds}
            editable={!isRunning && !locked}
            onCommit={(raw) => commitProjectTime(pt.project_id, raw)}
          />
        ))}
      </div>
    )}
    </>
  );
}

function ProjectBreakdownRow({ project, seconds, editable, onCommit }) {
  const orig = fmtSecondsAsHHMM(seconds);
  const [val, setVal] = useState(orig);
  useEffect(() => { setVal(orig); }, [orig]);
  return (
    <div className="wf-entry-proj-row">
      <span className="wf-entry-proj-connector" aria-hidden="true" />
      <span className="wf-entry-proj-name">{project?.name || "Projekt"}</span>
      <span className="wf-entry-fill" />
      {editable ? (
        <input
          className="wf-time-input wf-entry-proj-input"
          value={val}
          maxLength={5}
          onChange={(e) => setVal(e.target.value)}
          onBlur={(e) => {
            const norm = parseTimeDigits(e.target.value);
            if (norm == null || norm === orig) { setVal(orig); return; }
            const ok = onCommit(e.target.value);
            if (ok) {
              setVal(norm);
            } else {
              setVal(orig); // validation rejected (e.g. exceeds entry total)
            }
          }}
          onKeyDown={(e) => { if (e.key === "Enter") e.target.blur(); }}
        />
      ) : (
        <span className="wf-entry-proj-time">{orig}</span>
      )}
    </div>
  );
}

export function HoursAccountYearView({ entries, holidayEntries, vacationEntries, weeklyHoursTarget, today, user, hideTotalUnit, onSaveUser, showProjectSplit, headerExtra, semesterBreaks = [], viewYear, setViewYear, minYear, maxYear, projects = [] }) {
  // Angeklickte KW -> Detail-Popup (Tage der Woche + Projektaufteilung).
  const [weekDetail, setWeekDetail] = useState(null);
  // Only a still-running entry needs a live "now". Reading Date.now() in the render
  // body would invalidate the memos below on EVERY render (e.g. each keystroke in
  // the Soll field) and re-scan every entry, so it only ticks while it has to.
  const hasLiveEntry = (entries || []).some((e) => !e.ended_at);
  const [nowMs, setNowMs] = useState(() => Date.now());
  useEffect(() => {
    if (!hasLiveEntry) return;
    setNowMs(Date.now());
    const id = setInterval(() => setNowMs(Date.now()), 1000);
    return () => clearInterval(id);
  }, [hasLiveEntry]);

  // Per-day tracked totals (real entries + already-credit-adjusted synthetic
  // holiday + vacation entries). Sums respect each entry's local date.
  const dayTracked = useMemo(() => {
    const curMondayTs = getMonday(today).getTime();
    const holSet = holidayDateSet(holidayEntries);
    const map = new Map();
    for (const e of [...entries, ...holidayEntries, ...vacationEntries]) {
      const d = new Date(e.started_at);
      const monday = getMonday(d);
      if (monday.getTime() > curMondayTs) continue; // keep the current week, drop only future
      if (!countsAsTracked(e, holSet)) continue;
      const key = formatISODate(d);
      map.set(key, (map.get(key) || 0) + entryDurationMs(e, nowMs));
    }
    return map;
  }, [entries, holidayEntries, vacationEntries, today, nowMs]);

  const isWorkingStudent = user?.role === "working_student" && onSaveUser;

  // Earliest tracked Monday → list of weeks (Monday Dates). Everyone sees completed
  // weeks up to the current one; werkstudenten additionally see the current + future
  // weeks (through the end of next year) so the admin can enter a Soll ahead of time.
  const weekTimeline = useMemo(() => {
    const curMondayTs = getMonday(today).getTime();
    let firstMondayTs = Infinity;
    for (const e of entries) {
      const ts = getMonday(new Date(e.started_at)).getTime();
      if (ts >= curMondayTs) continue;
      if (ts < firstMondayTs) firstMondayTs = ts;
    }
    if (firstMondayTs === Infinity) {
      if (!isWorkingStudent) return [];
      firstMondayTs = curMondayTs; // no tracked data yet — start from the current week
    }
    // Non-werkstudenten see completed weeks + the current (running) week;
    // werkstudenten additionally see future weeks for planning.
    let endTs = isWorkingStudent
      ? getMonday(new Date(today.getFullYear() + 1, 11, 28)).getTime()
      : curMondayTs;
    // Employment end caps the timeline — no weeks after the person has left.
    if (user?.employment_end) {
      endTs = Math.min(endTs, getMonday(parseISODateLocal(user.employment_end)).getTime());
    }
    const list = [];
    const cursor = new Date(firstMondayTs);
    while (cursor.getTime() <= endTs) {
      list.push(new Date(cursor));
      cursor.setDate(cursor.getDate() + 7);
    }
    return list;
  }, [entries, today, isWorkingStudent, user]);

  // Per-day Soll: respects user pattern + per-week overrides. Falls back to 5×weeklyHoursTarget
  // spread across Mo-Fr when no user is supplied (e.g. legacy callers).
  const targetForDay = (date) => {
    if (user) return dailyTargetMs(user, date);
    const wd = isoWeekday(date);
    if (wd >= 6) return 0;
    return (weeklyHoursTarget / 5) * 3600000;
  };

  // Year navigation is controlled by the parent (shared across all tabs).
  const canPrev = viewYear > minYear;
  const canNext = viewYear < maxYear;

  // Group completed weeks by month. Werkstudenten need the exact per-month Soll,
  // so a week spanning two months is prorated and appears in BOTH months. For
  // everyone else the split is irrelevant (no hours are submitted), so each KW
  // appears exactly once — in the month that holds most of its days.
  const monthGroups = useMemo(() => {
    const curMondayTs = getMonday(today).getTime();
    const empEnd = user?.employment_end || null;
    const empStart = user?.employed_since || null;
    const holSet = holidayDateSet(holidayEntries);
    const overrides = user?.weekly_hours_overrides || {};
    const byMonth = new Map();
    const add = (m, wk) => { if (!byMonth.has(m)) byMonth.set(m, []); byMonth.get(m).push(wk); };
    for (const monday of weekTimeline) {
      const weekKey = weekKeyOf(monday);
      const ts = monday.getTime();
      const week = getWeekNumber(monday);
      const completed = ts < curMondayTs; // future/current weeks: no +/- yet, just the Soll
      const isCurrent = ts === curMondayTs;
      const perMonth = new Map();
      for (let i = 0; i < 7; i++) {
        const day = addDays(monday, i);
        if (day.getFullYear() !== viewYear) continue;
        const iso = formatISODate(day);
        if (empStart && iso < empStart) continue; // before joining
        if (empEnd && iso > empEnd) continue;      // after leaving
        const m = day.getMonth();
        const tracked = dayTracked.get(iso) || 0;
        const target = targetForDay(day);
        const cur = perMonth.get(m) || { tracked: 0, target: 0, days: 0, nonHolWd: 0 };
        cur.tracked += tracked;
        cur.target += target;
        cur.days += 1;
        const wd = day.getDay();
        if (wd !== 0 && wd !== 6 && !holSet.has(iso)) cur.nonHolWd += 1;
        perMonth.set(m, cur);
      }
      if (perMonth.size === 0) continue;
      if (isWorkingStudent) {
        // Werkstudenten: a week that spans two months appears in BOTH months, each
        // row showing only that month's portion of Soll + Getrackt.
        const split = perMonth.size > 1;
        // Auto-split rule for weeks from today onward (never for weeks the user has
        // already set portions for): the whole week's Soll lands in the month that
        // contains Wednesday — UNLESS a holiday there means 8-8-4 no longer fits, in
        // which case the hours stay split (prorated) across both months.
        const future = ts >= curMondayTs;
        const hasPortionOverride = [...perMonth.keys()].some((m) => overrides[`${weekKey}#${m}`] != null);
        if (split && future && !hasPortionOverride) {
          let V = 0;
          for (const [, s] of perMonth) V += s.target;
          if (V > 0) {
            const wedMonth = addDays(monday, 2).getMonth();
            const winM = perMonth.has(wedMonth)
              ? wedMonth
              : [...perMonth.entries()].sort((a, b) => b[1].nonHolWd - a[1].nonHolWd)[0][0];
            const daysNeeded = Math.ceil(V / (8 * 3600000));
            if ((perMonth.get(winM).nonHolWd || 0) >= daysNeeded) {
              for (const [m, s] of perMonth) s.target = (m === winM) ? V : 0;
            }
            // else: a holiday broke it → keep the prorated per-month targets.
          }
        }
        for (const [m, s] of perMonth) {
          add(m, { ts, week, weekKey, completed, isCurrent, split, tracked: s.tracked, target: s.target, diff: s.tracked - s.target });
        }
      } else {
        // Everyone else: one row per week — in the SAME month the self modal
        // groups it into (Thursday rule via monthOfWeek), so both views always
        // agree. Only when the Thursday falls into the neighbouring YEAR
        // (boundary week of this year-scoped view) does the in-year month with
        // the most days keep the row.
        let bestM = null, bestDays = -1, tracked = 0, target = 0;
        for (const [m, s] of perMonth) {
          tracked += s.tracked;
          target += s.target;
          if (s.days > bestDays) { bestDays = s.days; bestM = m; }
        }
        const thu = monthOfWeek(monday);
        const rowMonth = thu.year === viewYear && perMonth.has(thu.month) ? thu.month : bestM;
        add(rowMonth, { ts, week, weekKey, completed, isCurrent, split: false, tracked, target, diff: tracked - target });
      }
    }
    return [...byMonth.entries()]
      .sort((a, b) => a[0] - b[0])
      .map(([month, weeks]) => {
        weeks.sort((a, b) => a.ts - b.ts);
        // +/- balance counts only completed weeks; the planned Soll total covers all.
        const done = weeks.filter((w) => w.completed);
        const totalTracked = done.reduce((s, w) => s + w.tracked, 0);
        const totalTarget = done.reduce((s, w) => s + w.target, 0);
        const totalSollAll = weeks.reduce((s, w) => s + w.target, 0);
        return {
          month,
          weeks,
          totalTracked,
          totalTarget,
          totalSollAll,
          totalDiff: totalTracked - totalTarget,
        };
      });
  }, [weekTimeline, dayTracked, viewYear, user, weeklyHoursTarget, isWorkingStudent, today]);

  const yearDiff = monthGroups.reduce((s, g) => s + g.totalDiff, 0);

  // Werkstudenten: mark KW rows that fall inside a "Semesterferien" roadmap span
  // (they may work more hours then) with a light background. Spans are stored
  // with ISO week-YEARS (Roadmap), so callers must pass the ISO year too.
  const inSemesterBreak = (year, week) => isWorkingStudent && semesterBreaks.some(
    (r) => (year > r.start_year || (year === r.start_year && week >= r.start_week)) &&
           (year < r.end_year || (year === r.end_year && week <= r.end_week))
  );

  // Split the viewed year's worked time into project-allocated vs. internal
  // (worked time not booked on any project). Only real "work" entries count.
  const projectSplit = useMemo(() => {
    let work = 0, project = 0;
    for (const e of entries) {
      const d = new Date(e.started_at);
      if (d.getFullYear() !== viewYear) continue;
      if ((e.kind || "work") === "work") work += entryDurationMs(e, nowMs);
      if (Array.isArray(e.project_times)) {
        for (const pt of e.project_times) project += (pt.seconds || 0) * 1000;
      }
    }
    const cappedProject = Math.min(project, work);
    return { work, project: cappedProject, internal: Math.max(0, work - cappedProject) };
  }, [entries, viewYear, nowMs]);
  const splitPct = (ms) => (projectSplit.work > 0 ? Math.round((ms / projectSplit.work) * 100) : 0);

  const [editing, setEditing] = useState(null);    // per-row id (unique even for split weeks)
  const [editingKey, setEditingKey] = useState(null); // the week's override key to write
  const [editValue, setEditValue] = useState("");
  const [saveError, setSaveError] = useState("");

  const startEdit = (rowId, overrideKey, currentTargetMs) => {
    const ov = user?.weekly_hours_overrides?.[overrideKey];
    // Prefill with the stored override, or the row's current (auto) value in hours.
    const fallback = Math.round((currentTargetMs / 3600000) * 100) / 100;
    setEditValue(String(ov != null ? ov : fallback));
    setEditingKey(overrideKey);
    setEditing(rowId);
  };
  const commitEdit = async () => {
    if (!editing || !editingKey) return;
    const overrides = { ...(user?.weekly_hours_overrides || {}) };
    const raw = String(editValue).trim().replace(",", ".");
    const v = parseFloat(raw);
    // Empty input clears the override (back to the base); any number >= 0 (incl. 0)
    // is stored as this week's Soll.
    if (raw !== "" && Number.isFinite(v) && v >= 0) {
      overrides[editingKey] = v;
    } else {
      delete overrides[editingKey];
    }
    const key = editingKey;
    setEditing(null);
    setEditingKey(null);
    setSaveError("");
    try { await onSaveUser(user.id, { weekly_hours_overrides: overrides }); }
    catch (e) {
      // Surface the failure — before, the value silently vanished with only a
      // console line while the table kept showing the old Soll.
      console.error("Soll speichern fehlgeschlagen", key, e);
      setSaveError(`KW-Soll konnte nicht gespeichert werden (${key}) — bitte erneut versuchen.`);
    }
  };

  return (
    <div className="hours-account-list">
      <div className="hours-account-month-nav">
        <button type="button" className="wf-nav-btn" onClick={() => canPrev && setViewYear(viewYear - 1)} disabled={!canPrev} title="Vorheriges Jahr">
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="15 18 9 12 15 6" /></svg>
        </button>
        <span className="hours-account-month-label">{viewYear}</span>
        <button type="button" className="wf-nav-btn" onClick={() => canNext && setViewYear(viewYear + 1)} disabled={!canNext} title="Nächstes Jahr">
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="9 18 15 12 9 6" /></svg>
        </button>
        {headerExtra}
        <span className={`hours-account-month-total ${signClass(yearDiff)}`}>
          {fmtSignedDuration(yearDiff).replace(" h", "")}
          {!hideTotalUnit && <span className="hours-account-total-unit">h</span>}
        </span>
      </div>

      {saveError && <div className="form-error">{saveError}</div>}

      {weekDetail && (
        <WeekDetailModal
          week={weekDetail}
          entries={entries}
          holidayEntries={holidayEntries}
          vacationEntries={vacationEntries}
          dayTracked={dayTracked}
          nowMs={nowMs}
          today={today}
          projects={projects}
          onClose={() => setWeekDetail(null)}
        />
      )}

      {showProjectSplit && projectSplit.work > 0 && (
        <div className="hours-account-split">
          <div className="hours-account-split-row is-project">
            <span className="hours-account-split-label">Projekte</span>
            <span className="hours-account-split-val">{fmtClock(projectSplit.project)} h</span>
            <span className="hours-account-split-pct">{splitPct(projectSplit.project)} %</span>
          </div>
          <div className="hours-account-split-row is-internal">
            <span className="hours-account-split-label">Intern</span>
            <span className="hours-account-split-val">{fmtClock(projectSplit.internal)} h</span>
            <span className="hours-account-split-pct">{splitPct(projectSplit.internal)} %</span>
          </div>
        </div>
      )}

      {/* display:contents on desktop (layout unchanged); on mobile this becomes
          the single horizontal scroll container so head + body scroll together. */}
      <div className="ha-scroll-x">
      <div className="ha-head-wrap">
        <table className="ha-table">
          <colgroup>
            <col style={{ width: 30 }} />
            <col style={{ width: 42 }} />
            {isWorkingStudent && <col style={{ width: 88 }} />}
            {isWorkingStudent
              ? Array.from({ length: 4 }).map((_, i) => <col key={i} style={{ width: 118 }} />)
              : (<><col style={{ width: 86 }} /><col style={{ width: 116 }} /></>)}
            <col />
          </colgroup>
          <thead>
            <tr>
              <th className="ha-th-month" />
              <th className="ha-th-center ha-th-week">KW</th>
              {isWorkingStudent && <th className="ha-th-right">Soll</th>}
              <th className="ha-th-right">Getrackt</th>
              <th className="ha-th-right">+/-</th>
              {isWorkingStudent && <th className="ha-th-right">Gesamt</th>}
              {isWorkingStudent && <th className="ha-th-right">Ges. Soll</th>}
              <th className="ha-fill" />
            </tr>
          </thead>
        </table>
      </div>
      <div className="ha-table-wrap">
        <table className="ha-table">
          <colgroup>
            <col style={{ width: 30 }} />
            <col style={{ width: 42 }} />
            {isWorkingStudent && <col style={{ width: 88 }} />}
            {isWorkingStudent
              ? Array.from({ length: 4 }).map((_, i) => <col key={i} style={{ width: 118 }} />)
              : (<><col style={{ width: 86 }} /><col style={{ width: 116 }} /></>)}
            <col />
          </colgroup>
          <tbody>
            {monthGroups.length === 0 && (
              <tr><td className="ha-empty" colSpan={isWorkingStudent ? 8 : 5}>Keine Wochen in {viewYear}.</td></tr>
            )}
            {monthGroups.map((g) => (
              <React.Fragment key={g.month}>
                {g.weeks.map((w, i) => {
                  const rowId = `${w.weekKey}-${g.month}`;
                  // Every row (each week's portion in a given month) is edited & stored
                  // independently — the entered value applies only to that portion's days.
                  const overrideKey = `${w.weekKey}#${g.month}`;
                  const overridden = user?.weekly_hours_overrides?.[overrideKey] != null;
                  const isEditing = editing === rowId;
                  const isLast = i === g.weeks.length - 1;
                  // ISO week-year of the row's Monday, NOT viewYear: the Dec row
                  // of a KW 1 (or the Jan row of a KW 52/53) belongs to the
                  // neighbouring ISO year and was matched against the wrong one.
                  const ferien = inSemesterBreak(getISOWeekYear(new Date(w.ts)), w.week);
                  // Whole-KW Soll above 20 h → Werkstudenten-Warnung (26-Wochen-
                  // Regel). weekTargetMs covers the FULL week — the old per-year
                  // portion sum missed weeks split across the year boundary.
                  const overWs = isWorkingStudent && Math.round(weekTargetMs(user, new Date(w.ts)) / 60000) > 20 * 60;
                  return (
                    <tr key={`${g.month}-${w.ts}`} className={`ha-row ${isLast ? "ha-row-monthend" : ""} ${w.split ? "ha-row-split" : ""} ${ferien ? "ha-row-break" : ""} ${w.completed ? "" : "ha-row-future"}`}>
                      {i === 0 && (
                        <td className="ha-month-cell" rowSpan={g.weeks.length}>
                          <span className="ha-month-label">{MONTH_NAMES[g.month]}</span>
                        </td>
                      )}
                      <td className="ha-week">
                        <button
                          type="button"
                          className="ha-week-btn"
                          onClick={() => setWeekDetail(w)}
                          title={`KW ${w.week} im Detail: Tage, Projekte, Summe`}
                        >{pad2(w.week)}</button>
                      </td>
                      {isWorkingStudent && (
                        <td className={`ha-num ha-target ${overridden ? "is-override" : ""} ${overWs ? "is-over-ws" : ""}`}>
                          {isEditing ? (
                            <input
                              autoFocus
                              type="text"
                              inputMode="decimal"
                              value={editValue}
                              onChange={(e) => setEditValue(e.target.value)}
                              onBlur={commitEdit}
                              onKeyDown={(e) => {
                                if (e.key === "Enter") { e.preventDefault(); commitEdit(); }
                                else if (e.key === "Escape") { e.preventDefault(); setEditing(null); setEditingKey(null); }
                              }}
                              className="hours-account-target-input"
                            />
                          ) : (
                            <button
                              type="button"
                              className="hours-account-target-btn"
                              onClick={() => startEdit(rowId, overrideKey, w.target)}
                              title={`${w.split
                                ? "Soll-Anteil dieses Monats (unabhängig editierbar)"
                                : (overridden ? "KW-Soll überschrieben — Klick zum Ändern" : "Klicken um KW-Soll zu überschreiben")}${overWs ? " · Über 20 h Soll in dieser KW — Werkstudenten dürfen das nur in max. 26 Wochen/Jahr (Semesterferien)." : ""}`}
                            >{fmtClock(w.target)}</button>
                          )}
                        </td>
                      )}
                      <td className="ha-num">{(w.completed || w.isCurrent) ? fmtClock(w.tracked) : ""}</td>
                      {/* The running week shows a provisional +/- (against the full week's
                          Soll) in the same dimmed style as its other values; only weeks
                          without any data yet keep the dash. */}
                      <td className="ha-num ha-diff">{(w.completed || w.isCurrent) ? <SignedDiff ms={w.diff} /> : <span className="ha-future-dash">—</span>}</td>
                      {isWorkingStudent && <td className="ha-num ha-total">{isLast ? fmtClock(g.totalTracked) : ""}</td>}
                      {isWorkingStudent && <td className="ha-num ha-total">{isLast ? fmtClock(g.totalSollAll) : ""}</td>}
                      <td className="ha-fill" />
                    </tr>
                  );
                })}
              </React.Fragment>
            ))}
          </tbody>
        </table>
      </div>
      </div>
    </div>
  );
}

/* Detail einer Kalenderwoche: pro Tag die erfasste Zeit und wie viel davon auf
   welches Projekt lief, plus die Wochensumme. Rechnet NICHT selbst nach, sondern
   nutzt dieselben Werte wie die Tabelle (dayTracked/targetForDay) — sonst könnten
   Popup und Zeile sich widersprechen. */
function WeekDetailModal({ week, entries, holidayEntries, vacationEntries, dayTracked, nowMs, today, projects, onClose }) {
  const projById = useMemo(() => {
    const m = {};
    for (const p of projects || []) m[p.id] = p;
    return m;
  }, [projects]);

  // Die angezeigte Woche ist blätterbar — Startpunkt ist die angeklickte KW.
  const [mondayTs, setMondayTs] = useState(week.ts);
  const monday = useMemo(() => new Date(mondayTs), [mondayTs]);
  const weekNo = useMemo(() => getWeekNumber(monday), [monday]);
  const step = (n) => setMondayTs(addDays(monday, n * 7).getTime());

  // Grenzen: nicht vor die erste erfasste Woche und nicht in die Zukunft —
  // dort gibt es per Definition nichts zu sehen.
  const firstMondayTs = useMemo(() => {
    let min = Infinity;
    for (const e of entries || []) {
      const ts = getMonday(new Date(e.started_at)).getTime();
      if (ts < min) min = ts;
    }
    return min;
  }, [entries]);
  const canPrev = mondayTs > firstMondayTs;
  const canNext = mondayTs < getMonday(today).getTime();

  const days = useMemo(() => {
    // Für die Balken dieselben Quellen wie dayTracked (inkl. Feiertags-/Urlaubs-
    // Gutschrift, Krank-auf-Feiertag gefiltert) — sonst passt die Höhe nicht zur
    // Tagessumme darunter.
    const holSet = holidayDateSet(holidayEntries || []);
    const credits = [...(holidayEntries || []), ...(vacationEntries || [])];
    const out = [];
    for (let i = 0; i < 7; i++) {
      const date = addDays(monday, i);
      const iso = formatISODate(date);
      const dayEntries = (entries || []).filter((e) => formatISODate(new Date(e.started_at)) === iso);
      const byKind = { work: 0, vacation: 0, sick: 0, holiday: 0 };
      for (const e of [...dayEntries, ...credits.filter((c) => formatISODate(new Date(c.started_at)) === iso)]) {
        if (!countsAsTracked(e, holSet)) continue;
        byKind[kindOf(e)] += entryDurationMs(e, nowMs);
      }
      const perProject = new Map();
      for (const e of dayEntries) {
        for (const pt of e.project_times || []) {
          if (!pt.seconds) continue;
          perProject.set(pt.project_id, (perProject.get(pt.project_id) || 0) + pt.seconds * 1000);
        }
      }
      const kinds = [...new Set(dayEntries.map((e) => kindOf(e)).filter((k) => k !== "work"))];
      const projectMs = [...perProject.values()].reduce((s, ms) => s + ms, 0);
      out.push({
        iso, date, byKind, projectMs,
        tracked: dayTracked.get(iso) || 0,
        kinds,
        projects: [...perProject.entries()]
          .map(([id, ms]) => ({ id, ms, project: projById[id] }))
          .sort((a, b) => b.ms - a.ms),
      });
    }
    return out;
  }, [monday, entries, holidayEntries, vacationEntries, dayTracked, nowMs, projById]);

  // Mini-Graph oben: alle 7 Tage (auch leere — sonst verliert die Woche ihre
  // Form), Skala wie im Zeit-Bereich mindestens 8 h.
  const chartMax = useMemo(
    () => Math.max(8 * 3600000, ...days.map((d) => d.tracked)),
    [days]
  );
  // Die Liste darunter zeigt nur Tage MIT erfasster Zeit.
  const listDays = useMemo(() => days.filter((d) => d.tracked > 0), [days]);

  const totals = useMemo(() => {
    const perProject = new Map();
    let tracked = 0, projectMs = 0;
    for (const d of days) {
      tracked += d.tracked;
      for (const p of d.projects) {
        projectMs += p.ms;
        perProject.set(p.id, (perProject.get(p.id) || 0) + p.ms);
      }
    }
    return {
      tracked, projectMs,
      internal: Math.max(0, tracked - projectMs),
      projects: [...perProject.entries()]
        .map(([id, ms]) => ({ id, ms, project: projById[id] }))
        .sort((a, b) => b.ms - a.ms),
    };
  }, [days, projById]);

  const projLabel = (p, id) => {
    if (!p) return `Projekt #${id}`;
    return p.abbreviation ? `${p.abbreviation} · ${p.name}` : p.name;
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content ha-week-modal" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2 className="modal-title ha-week-modal-title">
            <button
              type="button" className="wf-nav-btn" disabled={!canPrev}
              onClick={() => canPrev && step(-1)} title="Vorherige KW"
            >
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="15 18 9 12 15 6" /></svg>
            </button>
            <span className="ha-week-modal-kw">KW {pad2(weekNo)}</span>
            <button
              type="button" className="wf-nav-btn" disabled={!canNext}
              onClick={() => canNext && step(1)} title="Nächste KW"
            >
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="9 18 15 12 9 6" /></svg>
            </button>
            <span className="ha-week-modal-range">
              {fmtDateDE(formatISODate(monday))} – {fmtDateDE(formatISODate(addDays(monday, 6)))}
            </span>
          </h2>
          <button className="modal-close-btn" onClick={onClose} title="Schließen">&times;</button>
        </div>

        <div className="ha-week-chart">
          {days.map((d) => (
            <div
              key={d.iso}
              className={`ha-week-chart-col ${d.tracked > 0 ? "" : "is-empty"}`}
              title={`${WEEKDAYS[(d.date.getDay() + 6) % 7]}, ${fmtDateDE(d.iso)} — ${d.tracked > 0 ? `${fmtDuration(d.tracked)} h` : "nichts erfasst"}`}
            >
              <div className="ha-week-chart-zone">
                <div className="wf-chart-bar" style={{ height: `${d.tracked > 0 ? Math.max((d.tracked / chartMax) * 100, 2) : 0}%` }}>
                  {barSegments(d.byKind, d.projectMs, d.tracked).map((s) => (
                    <div key={s.key} className="wf-chart-seg" style={{ height: `${s.pct}%`, background: s.color }} />
                  ))}
                </div>
              </div>
              <span className="ha-week-chart-day">{WEEKDAYS[(d.date.getDay() + 6) % 7]}</span>
            </div>
          ))}
        </div>

        <div className="ha-week-days">
          {listDays.length === 0 && (
            <div className="ha-week-empty">In dieser Woche wurde nichts erfasst.</div>
          )}
          {listDays.map((d) => {
            return (
              <div key={d.iso} className="ha-week-day">
                <div className="ha-week-day-head">
                  <span className="ha-week-day-name">
                    {WEEKDAYS_LONG[(d.date.getDay() + 6) % 7]}
                    <span className="ha-week-day-date">{fmtDateDE(d.iso)}</span>
                  </span>
                  <span className="ha-week-day-sum">
                    {d.tracked > 0 ? `${fmtDuration(d.tracked)} h` : "—"}
                  </span>
                </div>
                {d.kinds.length > 0 && (
                  <div className="ha-week-kinds">
                    {d.kinds.map((k) => (
                      <span key={k} className="ha-week-kind" style={{ color: KINDS[k].color, borderColor: KINDS[k].color }}>
                        {KINDS[k].label}
                      </span>
                    ))}
                  </div>
                )}
                {d.projects.length > 0 && (
                  <ul className="ha-week-projects">
                    {d.projects.map((p) => (
                      <li key={p.id}>
                        <span className="ha-week-proj-name">{projLabel(p.project, p.id)}</span>
                        <span className="ha-week-proj-val">{fmtDuration(p.ms)} h</span>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            );
          })}
        </div>

        <div className="ha-week-total">
          <div className="ha-week-total-row is-head">
            <span>Woche gesamt</span>
            <span>{fmtDuration(totals.tracked)} h</span>
          </div>
          {totals.projects.map((p) => (
            <div key={p.id} className="ha-week-total-row">
              <span className="ha-week-proj-name">{projLabel(p.project, p.id)}</span>
              <span>{fmtDuration(p.ms)} h</span>
            </div>
          ))}
          <div className="ha-week-total-row is-rest">
            <span>Ohne Projekt</span>
            <span>{fmtDuration(totals.internal)} h</span>
          </div>
        </div>
      </div>
    </div>
  );
}


export function HoursAccountView({ entries, holidayEntries, vacationEntries, weeklyHoursTarget, today, user }) {
  // See HoursAccountYearView: keep `nowMs` constant unless something is running.
  const hasLiveEntry = (entries || []).some((e) => !e.ended_at);
  const [nowMs, setNowMs] = useState(() => Date.now());
  useEffect(() => {
    if (!hasLiveEntry) return;
    setNowMs(Date.now());
    const id = setInterval(() => setNowMs(Date.now()), 1000);
    return () => clearInterval(id);
  }, [hasLiveEntry]);
  const isWorkingStudent = user?.role === "working_student";

  // Build per-week rows. Werkstudenten see the current and next week too (with no +/-)
  // so they can plan ahead against their Soll. Everyone else only sees completed weeks.
  const months = useMemo(() => {
    const curMondayTs = getMonday(today).getTime();
    // Non-werkstudenten: include the current (running) week; werkstudenten also
    // the next. addDays, NOT +7*86400000: the DST switch weeks are 167/169 h
    // long, so the raw-ms jump missed the next Monday-midnight timestamp there.
    let lastIncludedMondayTs = isWorkingStudent ? addDays(new Date(curMondayTs), 7).getTime() : curMondayTs;
    // Employment end caps the timeline — no weeks after the person has left.
    if (user?.employment_end) {
      lastIncludedMondayTs = Math.min(lastIncludedMondayTs, getMonday(parseISODateLocal(user.employment_end)).getTime());
    }

    const holSet = holidayDateSet(holidayEntries);
    const byWeek = new Map();
    let firstMondayTs = Infinity;
    for (const e of entries) {
      const ts = getMonday(new Date(e.started_at)).getTime();
      if (ts > lastIncludedMondayTs) continue;
      if (ts < firstMondayTs) firstMondayTs = ts;
      if (!countsAsTracked(e, holSet)) continue;
      byWeek.set(ts, (byWeek.get(ts) || 0) + entryDurationMs(e, nowMs));
    }
    if (firstMondayTs === Infinity) {
      if (!isWorkingStudent) return [];
      firstMondayTs = curMondayTs;
    }
    if (isWorkingStudent && firstMondayTs > curMondayTs) firstMondayTs = curMondayTs;
    for (const h of [...holidayEntries, ...vacationEntries]) {
      const ts = getMonday(new Date(h.started_at)).getTime();
      if (ts < firstMondayTs || ts > lastIncludedMondayTs) continue;
      byWeek.set(ts, (byWeek.get(ts) || 0) + entryDurationMs(h, nowMs));
    }

    const groups = [];
    let current = null;
    const cursor = new Date(firstMondayTs);
    while (cursor.getTime() <= lastIncludedMondayTs) {
      const ts = cursor.getTime();
      const tracked = byWeek.get(ts) || 0;
      const isCompleted = ts < curMondayTs;
      const target = weekTargetMs(user, new Date(cursor), weeklyHoursTarget);
      const w = {
        ts,
        monday: new Date(cursor),
        week: getWeekNumber(new Date(cursor)),
        tracked,
        target,
        diff: isCompleted ? tracked - target : 0,
        isCompleted,
      };
      // Same month rule as the admin year view: a week belongs to the month
      // holding most of its days (= the Thursday's month), so a KW whose Monday
      // is 30.03. shows under April in both views.
      const { month: m, year: y } = monthOfWeek(w.monday);
      const key = `${y}-${m}`;
      if (!current || current.key !== key) {
        current = { key, year: y, month: m, weeks: [], totalDiff: 0 };
        groups.push(current);
      }
      current.weeks.push(w);
      if (w.isCompleted) current.totalDiff += w.diff;
      cursor.setDate(cursor.getDate() + 7);
    }
    return groups;
  }, [entries, holidayEntries, vacationEntries, today, weeklyHoursTarget, nowMs, isWorkingStudent, user]);

  // newest month first; default to the most recent (which contains today / next week)
  const [idx, setIdx] = useState(months.length - 1);
  useEffect(() => { setIdx(months.length - 1); }, [months.length]);

  if (months.length === 0) {
    return <div className="wf-empty-week">Noch keine Wochen.</div>;
  }
  const safeIdx = Math.min(Math.max(idx, 0), months.length - 1);
  const g = months[safeIdx];
  const canPrev = safeIdx > 0;
  const canNext = safeIdx < months.length - 1;

  return (
    <div className={`hours-account-list ${isWorkingStudent ? "has-target" : "no-target"}`}>
      <div className="hours-account-month-nav">
        <button
          type="button"
          className="wf-nav-btn"
          onClick={() => canPrev && setIdx(safeIdx - 1)}
          disabled={!canPrev}
          title="Vorheriger Monat"
        >
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="15 18 9 12 15 6" /></svg>
        </button>
        <span className="hours-account-month-label">{MONTH_NAMES[g.month]} {g.year}</span>
        <button
          type="button"
          className="wf-nav-btn"
          onClick={() => canNext && setIdx(safeIdx + 1)}
          disabled={!canNext}
          title="Nächster Monat"
        >
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="9 18 15 12 9 6" /></svg>
        </button>
        <span className={`hours-account-month-total ${signClass(g.totalDiff)}`}>
          {fmtSignedDuration(g.totalDiff).replace(" h", "")}
          <span className="hours-account-total-unit">h</span>
        </span>
      </div>

      <div className="hours-account-row hours-account-row-head">
        <span>KW</span>
        <span>Getrackt</span>
        {isWorkingStudent && <span>Soll</span>}
        <span>+/-</span>
      </div>
      {g.weeks.map((w) => {
        const untracked = w.isCompleted && w.tracked === 0 && w.target > 0;
        return (
          <div key={w.ts} className="hours-account-row" title={untracked ? "Keine Zeit erfasst — zählt aktuell als volles Minus" : undefined}>
            <span className="hours-account-val hours-account-week">{pad2(w.week)}</span>
            <span className="hours-account-val">
              {untracked ? <span className="hours-account-untracked">nicht erfasst</span> : fmtClock(w.tracked)}
            </span>
            {isWorkingStudent && <span className="hours-account-val">{fmtClock(w.target)}</span>}
            <span className={`hours-account-val ${w.isCompleted ? signClass(w.diff) : ""}`}>
              {w.isCompleted ? fmtSignedDuration(w.diff).replace(/\s*h$/i, "") : "—"}
            </span>
          </div>
        );
      })}
    </div>
  );
}

function HoursAccountModal({ entries, holidayEntries, vacationEntries, weeklyHoursTarget, today, user, onClose }) {
  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content modal-wide stunden-modal" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2 className="modal-title">Stundenkonto</h2>
          <button className="modal-close-btn" onClick={onClose}>&times;</button>
        </div>
        <HoursAccountView
          entries={entries}
          holidayEntries={holidayEntries}
          vacationEntries={vacationEntries}
          weeklyHoursTarget={weeklyHoursTarget}
          today={today}
          user={user}
        />
      </div>
    </div>
  );
}

function rangesOverlap(aStart, aEnd, bStart, bEnd) {
  return aStart <= bEnd && aEnd >= bStart;
}

// `accountFor(year)` is the phase-scoped canonical account (see
// vacationAccountForPhase) — the modal must never compute a balance of its own.
// Self-service only: employees can request vacation here, nothing else —
// approving, editing and deleting are admin actions in the Personal area.
function VacationModal({ vacations, holidays, accountFor, onClose, onChanged }) {
  const todayISO = formatISODate(new Date());
  // No pre-selected range: selecting today by default painted today in the range
  // colour, which reads exactly like a "today" marker and confused people.
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [half, setHalf] = useState(false);
  const [pickEnd, setPickEnd] = useState(false); // false = next click starts a new range
  const [err, setErr] = useState("");
  const currentYear = new Date().getFullYear();
  const [viewYear, setViewYear] = useState(currentYear);

  // Range selection in the inline calendar (same behaviour as the Personal popup).
  const pickRangeDay = (y, m, d) => {
    const iso = `${y}-${pad2(m + 1)}-${pad2(d)}`;
    if (!pickEnd) { setFrom(iso); setTo(iso); }
    else if (iso < from) setFrom(iso);
    else setTo(iso);
    setPickEnd((p) => !p);
  };

  const request = async () => {
    if (!from || !to) { setErr("Bitte einen Zeitraum wählen"); return; }
    if (to < from) { setErr("Ende darf nicht vor dem Start liegen"); return; }
    // Client-side overlap check — cancelled/rejected requests don't block the
    // range (mirrors the server's overlap rule).
    const conflict = (vacations || []).find(
      (v) => v.status !== "rejected" && v.status !== "cancelled" && rangesOverlap(from, to, v.start_date, v.end_date)
    );
    if (conflict) {
      setErr("Dieser Zeitraum überschneidet sich mit einem bestehenden Urlaubsantrag.");
      return;
    }
    // Single day → half_day; range → half_end (last day is a half day).
    const halfDay = half && from === to;
    const halfEnd = half && from !== to;
    try {
      await createVacation(from, to, { half_day: halfDay, half_end: halfEnd });
      setFrom(""); setTo(""); setHalf(false); setPickEnd(false); setErr("");
      onChanged();
    } catch (e) { setErr(e.message); }
  };

  const statusLabel = (v) => {
    if (v.status === "rejected" || v.status === "cancelled") return "storniert";
    if (v.status === "requested") return "beantragt";
    return "genehmigt";
  };

  // ONE data source for everything the modal shows: besides the balance the
  // account also hands back the vacation records clipped to the employment phase
  // it scoped to. Year navigation, list and tiles all read from it, so they can
  // never contradict each other (the raw `vacations` prop still spans phases).
  const account = useMemo(() => accountFor(viewYear), [accountFor, viewYear]);
  const balance = account.balance;
  const scopedVacations = account.vacations;

  // Year navigation: show only requests that touch the selected year
  const availableYears = useMemo(() => {
    const years = new Set([currentYear]);
    for (const v of scopedVacations) {
      years.add(Number(v.start_date.slice(0, 4)));
      years.add(Number(v.end_date.slice(0, 4)));
    }
    // Planning never goes further back than 2024.
    return [...years].filter((y) => y >= 2024).sort((a, b) => a - b);
  }, [scopedVacations, currentYear]);

  const minYear = availableYears[0];
  const maxYear = availableYears[availableYears.length - 1];
  const canPrev = viewYear > minYear;
  const canNext = viewYear < Math.max(maxYear, currentYear + 1);

  const filteredVacations = useMemo(() => {
    const yStart = `${viewYear}-01-01`;
    const yEnd = `${viewYear}-12-31`;
    return scopedVacations
      .filter((v) => rangesOverlap(yStart, yEnd, v.start_date, v.end_date))
      .sort((a, b) => a.start_date.localeCompare(b.start_date));
  }, [scopedVacations, viewYear]);

  // Everything the tiles show comes straight from the canonical balance.
  const totals = balance;

  // Entitlement for the year currently navigated to (pro-rated for working students).
  const viewEntitlement = balance.entitlement;

  // Tiles use a decimal point (not a German comma) on purpose.
  const fmtDot = (n) => (Number.isInteger(n) ? String(n) : Number(n).toFixed(1));

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content modal-wide wf-vac-modal" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2 className="modal-title">Urlaubskonto</h2>
          <button className="modal-close-btn" onClick={onClose}>&times;</button>
        </div>

        <div className="wf-vac-new-card">
          <div className="wf-vac-new-grid">
            <div className="wf-vac-new-cal">
              <MiniCalendar
                currentYear={Number((from || todayISO).slice(0, 4))}
                currentMonth={Number((from || todayISO).slice(5, 7)) - 1}
                onSelect={pickRangeDay}
                rangeStart={from || null}
                rangeEnd={to || null}
                hideToday
              />
            </div>
            <div className="wf-vac-new-side">
              <div className="wf-vac-tiles">
                {/* Übertrag is shown even when there is none ("0/0") so the number of
                    tiles — and with it the column height — never changes. */}
                <div className="wf-vac-tile" title="Übertrag aus dem Vorjahr">
                  <span className="wf-vac-tile-label">Übertrag</span>
                  <span className={`wf-vac-tile-val ${totals.carryExpired ? "is-carry-expired" : ""}`}>
                    {fmtDot(totals.carryRemaining)}<span className="wf-vac-tile-sep">/</span>{fmtDot(totals.carryIn)}
                  </span>
                  {totals.carryIn > 0 && (
                    <span className="wf-vac-tile-sub">
                      {totals.carryExpired ? "verfallen" : `verfällt ${fmtDateDE(totals.expiresOn)}`}
                    </span>
                  )}
                </div>
                {/* `open` already treats every non-rejected request as used, so beantragte
                    Tage are deducted here — no separate "Beantragt" tile needed. */}
                <div className="wf-vac-tile" title="Offen / Jahresanspruch — ohne Übertrag (eigene Kachel), beantragte Tage bereits abgezogen">
                  <span className="wf-vac-tile-label">Anspruch</span>
                  <span className="wf-vac-tile-val">
                    {fmtDot(totals.annualRemaining)}<span className="wf-vac-tile-sep">/</span>{fmtDot(viewEntitlement)}
                  </span>
                </div>
              </div>
              <div className="wf-vac-new-actions">
                <label className="emp-halfday-toggle">
                  <input type="checkbox" checked={half} onChange={(e) => setHalf(e.target.checked)} />
                  <span>{from === to ? "Halber Tag" : "Letzter Tag halb"}</span>
                </label>
                <button type="button" className="btn btn-primary wf-vac-new-btn" onClick={request}>Beantragen</button>
              </div>
            </div>
          </div>
          {err && <div className="wf-form-error">{err}</div>}
        </div>

        <div className="wf-vac-year-nav">
          <button
            type="button"
            className="wf-nav-btn"
            onClick={() => canPrev && setViewYear(viewYear - 1)}
            disabled={!canPrev}
            title="Vorheriges Jahr"
          >
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="15 18 9 12 15 6" /></svg>
          </button>
          <span className="wf-vac-year-label">{viewYear}</span>
          <button
            type="button"
            className="wf-nav-btn"
            onClick={() => canNext && setViewYear(viewYear + 1)}
            disabled={!canNext}
            title="Nächstes Jahr"
          >
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="9 18 15 12 9 6" /></svg>
          </button>
        </div>

        <div className="wf-vac-list">
          <div className="wf-vac-row wf-vac-row-head">
            <span>Von</span>
            <span>Bis</span>
            <span>Tage</span>
            <span className="wf-vac-status-col">Status</span>
          </div>
          {filteredVacations.length === 0 && <div className="wf-empty-week">Kein Urlaub in {viewYear}.</div>}
          {filteredVacations.map((v) => {
            const yearStart = `${viewYear}-01-01`;
            const yearEnd = `${viewYear}-12-31`;
            const dispStart = v.start_date < yearStart ? yearStart : v.start_date;
            const dispEnd = v.end_date > yearEnd ? yearEnd : v.end_date;
            const days = countedVacDays(dispStart, dispEnd, holidays, v);
            const daysLabel = Number.isInteger(days) ? String(days) : days.toFixed(1).replace(".", ",");
            return (
              <div key={v.id} className="wf-vac-row">
                <span className="wf-vac-cell wf-vac-cell-muted">{fmtDateDE(dispStart)}</span>
                <span className="wf-vac-cell wf-vac-cell-muted">{dispStart === dispEnd ? "" : fmtDateDE(dispEnd)}</span>
                <span className="wf-vac-cell wf-vac-cell-muted wf-vac-cell-days">{daysLabel}</span>
                <span className="wf-vac-row-end">
                  <span className={`wf-vac-status is-${v.status}`}>{statusLabel(v)}</span>
                </span>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

/* KW dropdown — chevron-down, KW-only list, with prev/next year nav. */
export function WeekPicker({ periodStart, onPick }) {
  const [open, setOpen] = useState(false);
  const [pos, setPos] = useState(null);
  const [pickYear, setPickYear] = useState(getISOWeekYear(periodStart));
  const triggerRef = useRef(null);
  const popRef = useRef(null);

  // ISO week-YEAR, not calendar year: a Monday 29.-31.12. belongs to KW 1 of the
  // FOLLOWING year — with getFullYear the picker highlighted the wrong cell.
  const activeWeek = getWeekNumber(periodStart);
  const activeYear = getISOWeekYear(periodStart);

  useEffect(() => {
    if (!open || !triggerRef.current) { setPos(null); return; }
    const r = triggerRef.current.getBoundingClientRect();
    const POPUP_H = 300;
    const flipUp = r.bottom + POPUP_H + 8 > window.innerHeight && r.top > POPUP_H + 8;
    const top = flipUp ? Math.max(8, r.top - POPUP_H - 4) : r.bottom + 6;
    setPos({ top, left: r.left });
    setPickYear(getISOWeekYear(periodStart));
  }, [open, periodStart]);

  useEffect(() => {
    function onDoc(e) {
      if (triggerRef.current && !triggerRef.current.contains(e.target) &&
          popRef.current && !popRef.current.contains(e.target)) setOpen(false);
    }
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, []);

  const lastWeek = lastWeekOfYear(pickYear);
  const items = Array.from({ length: lastWeek }, (_, i) => i + 1);

  return (
    <>
      <span className="wf-week-label-wrap" ref={triggerRef}>
        <span className="wf-week-label">KW {activeWeek}</span>
        <button
          type="button"
          className="wf-nav-btn"
          onClick={() => setOpen((v) => !v)}
          title="Kalenderwoche wählen"
        >
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="6 9 12 15 18 9" /></svg>
        </button>
      </span>
      {open && pos && createPortal(
        <div ref={popRef} className="week-picker-dropdown" style={{ position: "fixed", top: pos.top, left: pos.left, zIndex: 1100 }}>
          <div className="week-picker-header">
            <button type="button" className="week-picker-nav" onClick={() => setPickYear(pickYear - 1)} title="Vorheriges Jahr">
              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="15 18 9 12 15 6" /></svg>
            </button>
            <span className="week-picker-year">{pickYear}</span>
            <button type="button" className="week-picker-nav" onClick={() => setPickYear(pickYear + 1)} title="Nächstes Jahr">
              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="9 18 15 12 9 6" /></svg>
            </button>
          </div>
          <div className="week-picker-grid">
            {(() => {
              const cols = 6;
              const rows = [];
              for (let i = 0; i < items.length; i += cols) rows.push(items.slice(i, i + cols));
              return rows.map((row, ri) => (
                <div key={ri} className="week-picker-row">
                  {row.map((k) => {
                    const mon = getMondayOfWeek(pickYear, k);
                    const isActive = pickYear === activeYear && k === activeWeek;
                    return (
                      <button
                        key={k}
                        type="button"
                        className={`week-picker-cell ${isActive ? "week-picker-cell-selected" : ""}`}
                        onClick={() => { onPick(mon); setOpen(false); }}
                        title={`KW ${k}`}
                      >
                        {pad2(k)}
                      </button>
                    );
                  })}
                </div>
              ));
            })()}
          </div>
        </div>,
        document.body
      )}
    </>
  );
}

/* Inline time picker — same look & feel as the appointment form's TimeInput.
   Reuses the .time-input / .time-dropdown CSS classes from AppointmentForm.css. */
function TimeDropdown({ value, onChange, placeholder = "HH:MM" }) {
  const [open, setOpen] = useState(false);
  const [pos, setPos] = useState(null);
  const triggerRef = useRef(null);
  const dropdownRef = useRef(null);

  useEffect(() => {
    if (!open || !triggerRef.current) { setPos(null); return; }
    const rect = triggerRef.current.getBoundingClientRect();
    setPos({ top: rect.bottom + 4, left: rect.left, width: rect.width });
  }, [open]);

  useEffect(() => {
    function handle(e) {
      if (
        triggerRef.current && !triggerRef.current.contains(e.target) &&
        dropdownRef.current && !dropdownRef.current.contains(e.target)
      ) setOpen(false);
    }
    document.addEventListener("mousedown", handle);
    return () => document.removeEventListener("mousedown", handle);
  }, []);

  // Fixed-position portal — page scrolling would detach it from the input;
  // close instead (scrolling inside the option list stays allowed).
  useEffect(() => {
    if (!open) return;
    const onScroll = (e) => {
      if (dropdownRef.current && e.target instanceof Node && dropdownRef.current.contains(e.target)) return;
      setOpen(false);
    };
    window.addEventListener("scroll", onScroll, true);
    window.addEventListener("resize", onScroll);
    return () => {
      window.removeEventListener("scroll", onScroll, true);
      window.removeEventListener("resize", onScroll);
    };
  }, [open]);

  return (
    <div className="time-input-wrap">
      <input
        ref={triggerRef}
        type="text"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        onFocus={() => setOpen(true)}
        // "9" / "930" beim Verlassen zu "09:00" / "09:30" ergaenzen — sonst
        // bliebe eine halbe Eingabe stehen und das Speichern schluege fehl.
        onBlur={() => { const norm = parseTimeDigits(value); if (norm && norm !== value) onChange(norm); }}
        placeholder={placeholder}
        className="time-input"
      />
      {open && pos && createPortal(
        <div
          ref={dropdownRef}
          className="time-dropdown"
          style={{ position: "fixed", top: pos.top, left: pos.left, width: pos.width }}
        >
          {TIME_DROPDOWN_OPTIONS.map((t) => (
            <button
              key={t}
              type="button"
              className={`time-option ${t === value ? "active" : ""}`}
              onClick={() => { onChange(t); setOpen(false); }}
            >
              {t}
            </button>
          ))}
        </div>,
        document.body
      )}
    </div>
  );
}

